/**
 * DealDrawer.tsx — the HubSpot-style deal view (2026-07-21, PR 3/5; drawer
 * refinements UX Wave 4 §4 same day): a `twoThirds` Drawer ("use 2/3 of the
 * screen when a deal is open") with a FROZEN header (contact name + stage +
 * entry tag + ONE quiet age line — see `stageAge`, which replaced three
 * separate statements of `daysInStage` on 2026-09-06). The old read-only
 * stage-chip strip (StageMap) is GONE — it was
 * not actionable and duplicated the header badge. Stage moves live in ONE
 * place now: the footer CTAs — the single most-likely next stage (the FIRST
 * `MANUAL_TARGETS` entry, mirroring the server's ordering) is the primary
 * "Move to <stage>" button, any remaining legal targets sit behind a
 * secondary ⋯ "More moves" menu — which since 2026-08-26 (§5) also carries
 * the test-record housekeeping rows (Mark / Unmark test record, above Close
 * as lost) and therefore ALWAYS renders — and a "Log activity" button opens
 * the composer MODAL (2026-08-27 restructure: the old "Activity" nav button
 * is GONE — the tabs are the navigation).
 * Every move still ROUTES up through onRequestMove so ONE owner (PipelineTab)
 * runs the right dialog per move channel — nothing posts from here (R41).
 *
 * ABOVE THE TABS, in order (2026-09-06): the ADMIN PLAN PRICE (first month and
 * plan value, ADMIN-only behind the Payments tab's own gate — see the `money`
 * block), the CALL-LIMIT banner (the auto-escalate / auto-park threshold, whose
 * per-owner tally lives at the top of the Activity tab), and the following.
 *
 * THE TABS THEMSELVES CARRY MARKERS (2026-09-06) — an amber triangle for
 * clinical attention, a dot for something outstanding, a number pill for N
 * things to read. See the `TAB MARKERS` block below for which signal earns
 * which, and `TabMarker` in ui/Tabs.tsx for why the severe one had to stop
 * being the only shape available. ⚠️ A marker's wording is APPENDED to its
 * tab's accessible name, so a spec looking a tab up by exact name has to
 * account for it.
 *
 * ABOVE THE TABS (2026-08-27, stage clarity §2): the ACTION-NEEDED banner, when
 * the server flags an outstanding ask on the deal. It sits outside the tab
 * strip on purpose — an admin opening the card has to see what they were asked
 * to do without first knowing which tab to look in, and the ask previously
 * existed only as one note buried in the activity feed. Shown to BOTH roles
 * (see DealActionBanner for why hiding a clinician's own outstanding request
 * from them would be the worse failure).
 *
 * Tabs, in order (no per-tab subtitle lines — the tab names the panel):
 *  - Overview — contact block, linked summaries, key dates. On a CLOSED deal it
 *    now leads with the OUTCOME BLOCK (stage clarity §1): the closure reason
 *    verbatim, who closed it and when, replacing the derived "Clinical status"
 *    chip — which reads `eligibility` and therefore called an engine-fired hard
 *    stop a clinician's rejection. It also carries (ADMIN only, 2026-08-27) the
 *    PHOTO CHASE count with its manual-follow-up prompt once the patient has
 *    been chased twice and still uploaded nothing. CTO decision: surface it to
 *    an admin and let a human decide — the journey is NEVER auto-closed on a
 *    missing photo. Since 2026-09-07 it also carries the PATIENT JOURNEY panel
 *    (JourneyPanel.tsx): one rail for the whole route, grouped into phases whose
 *    ORDER is derived from the data (review-before-payment on the self-serve
 *    lane, payment-before-review where the money landed first), with the
 *    outstanding step and the controls that perform it in an action band at its
 *    top. The Plan link section that used to end this tab MOVED into that band —
 *    same component, same single mount, same gate;
 *  - Questionnaire — the clinician's in-drawer form home (PR 4/5,
 *    QuestionnairePanel): shown whenever an assessment exists or a CLINICIAN
 *    can still start one on an open non-Clinical-review deal. Since the
 *    2026-08-27 restructure every read-only view here is the ANSWER SUMMARY
 *    (QuestionnaireSummary — the Review tab's structured Q/A sections, with
 *    the ACTUAL photo state and the consent tick-list), never a greyed form
 *    replica; only the clinician actually filling a draft gets the editable
 *    SinglePageForm. While the assessment is still DRAFT — so the Review tab
 *    below does not exist yet — this tab also carries the general assessment
 *    clinical-notes thread at the bottom (AssessmentNotesSection, its default
 *    "Clinical notes" name): the fill-on-behalf call workflow and a CLIN-03
 *    stopped-while-draft deal both need the running clinical record before
 *    there is any Review tab to host it; once Review exists it is the
 *    thread's SINGLE home and this mount disappears;
 *  - Review — the existing ReviewView mounted in-drawer for Clinical-review
 *    deals (approve/reject/prescribe; ADMIN sees them disabled — the server
 *    is CLINICIAN-only now). Since 2026-08-27 it is the complete clinical
 *    decision surface: the ASSESSMENT clinical-notes thread (list + composer)
 *    lives there below the decision actions — decide and document in one
 *    place. 2026-08-07: this drawer OWNS the key-alerts read
 *    (useMedicationSafety) rather than ReviewView, because the Review TAB
 *    carries a warning marker whenever the double-prescribing advisory has
 *    something to say — it has to be up before anyone opens the tab, and one
 *    read means the marker and the band can never disagree;
 *  - Payments — ADMIN-ONLY (hidden entirely for clinicians: the pipeline is
 *    money-free for them by construction; the money-redaction guard scans
 *    this drawer's DOM);
 *  - Activity — LOGGED ACTIVITY ONLY since the 2026-08-27 CTO drawer review:
 *    the notes, call attempts (with outcomes) and logged emails a PERSON sat
 *    down and recorded. The include list is LOGGED_KINDS, mirrored from
 *    clinic-service's `dealActivityCreate` WRITE contract so "a human logged
 *    it" has exactly one definition across the two services. Everything the
 *    SYSTEM wrote moved to the Audit log below. Before this the rule was
 *    `kind !== 'audit'`, which excluded one kind instead of including the
 *    logged ones — so every journey milestone and stage move piled in here
 *    while the Audit log showed a single row. The composer moved to the
 *    footer's "Log activity" MODAL (same DealActivityComposer, same POST
 *    lanes) and the clinical-note thread moved to the Review tab. A sticky
 *    not_eligible assessment (CLIN-03) shows the same Lost banner as
 *    Questionnaire;
 *  - History — "the same person can submit multiple times". TWO lists: the
 *    contact's PREVIOUS ATTEMPTS (2026-08-15 — earlier callback tickets and
 *    eligibility checks this card no longer points at) above their other deal
 *    CARDS. A repeat journey now re-points this one card instead of adding a
 *    sibling to the board, so the attempts list is the only place that history
 *    survives; the deals list stays for the sibling rows that predate that.
 *    Same not_eligible Lost banner when this deal stopped;
 *  - Audit log — a REAL TAB (UX Wave 9 §1; replaces Wave 4's separate
 *    header button + panel): FLOATED RIGHT in the same tab row (TabDef
 *    `floatRight`) so governance stays visually apart from the working tabs
 *    but renders in the SAME content area. Same feed data — no extra API call.
 *    Since the 2026-08-27 CTO drawer review it is the FULL SYSTEM/JOURNEY
 *    TRAIL — the exact COMPLEMENT of Activity's logged kinds, so the two tabs
 *    PARTITION the feed and a row removed from one is always relocated, never
 *    dropped: mini form submitted, questionnaire started/submitted, callback
 *    requested, every stage transition, system-sent emails, clinical decision
 *    beats, deal created. Its role gating is UNCHANGED (there was none — both
 *    roles saw it before and still do).
 * Note bodies are sanitised AGAIN client-side before rendering (defence in
 * depth — same rule as the callback timeline).
 *
 * FOOTER ACTIONS are the deal's dedicated moves: Recover lead (a Lost deal) and
 * Send for admin review (the clinician→admin handoff, 2026-08-07). Both are
 * their own endpoint rather than a MANUAL_TARGETS channel, and both run through
 * the SAME ConfirmDialog + required-reason pattern. Nothing here stops a plan:
 * a clinician never gets that control (money actions are ADMIN, in orders).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { dealPendingAdminReview, getDealDetail, markDealDuplicate, markDealTest, recoverDeal, reopenSupersededDeal } from '../api/deals';
import { lookupStaffUsers } from '../api/identity';
import { getStaffQueue, markOrderTest } from '../api/orders';
import type { DealActivityEntry, DealCard, DealDetail, DealPriorAttempt, DealTestCascade, StaffOrder, UserRole } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { formatPence, formatUtcDate, formatUtcDateTime } from '../lib/format';
import { noteByline } from '../lib/noteByline';
import { formatCallWindow } from '../lib/timeBlocks';
import { sanitizeNoteHtml } from '../lib/sanitizeHtml';
import { AssessmentNotesSection } from '../clinicians/AssessmentNotes';
import { keyAlertFlag } from '../clinicians/KeyAlerts';
import {
  PrescribingFlagChip,
  PrescribingReasonNotice,
  prescribingFlagsOf,
  prescribingTabWarning,
} from '../clinicians/prescribingFlags';
import { ReviewView, type ActionNotice } from '../clinicians/ReviewView';
import { useMedicationSafety } from '../clinicians/useMedicationSafety';
import { SectionTitle, Row } from '../staff/ui';
import { Alert, Badge, Button, Drawer, ErrorState, Help, WarningIcon } from '../ui/kit';
import { NotRecorded } from '../ui/PrescribedItems';
import { ConfirmDialog, DialogShell } from '../ui/ConfirmDialog';
import { DrawerSkeleton } from '../ui/skeletons';
import { Menu, MenuItem } from '../ui/Menu';
import { DealActivityComposer } from './DealActivityComposer';
import { EngagementPanel } from './EngagementPanel';
import { ContactHistoryView, useDealHistory } from './HistoryPanel';
import {
  MATCH_BASIS_LABEL,
  ReturningSummary,
  SIBLING_BASIS_LABEL,
  SIBLING_BASIS_NOTE,
  SUPERSEDED_NO_MOVES,
  SubmittedAsLine,
  SupersededBanner,
  SupersededMarker,
  isSuperseded,
} from './returning';
import { HoldStatusPanel } from './HoldStatusPanel';
import { JourneyPanel } from './JourneyPanel';
import { LeadCallDialog } from './LeadCallDialog';
import { MarketingConsentSection } from './MarketingConsentSection';
import { MarkTestCascadeDialog, cascadeIsEventful, type CascadeDirection } from './markTestCascade';
import { Tabs, TabPanel, type TabDef } from '../ui/Tabs';
import { toneFor } from '../ui/tones';
import { activeRoundAttempts, dealName } from './PipelineBoard';
import { QuestionnairePanel } from './QuestionnairePanel';
import { ClinicalStatusChip, EntryPathChip, ReviewParkedChip, clinicalStatusFor, isReviewParked } from './statusChips';
import {
  AbsorbedPaymentBanner,
  DealLinkButton,
  DuplicateMarker,
  DuplicatePaymentBanner,
  DuplicateTrailSummary,
  MergedDuplicatesList,
  MergedIntoBanner,
  absorbedRowLink,
  countAbsorbRows,
  duplicateTrailRow,
  isMergedDuplicate,
  needsPaymentReview,
  paidMergedDuplicates,
} from './duplicateSurface';
import { duplicateRefusal, MarkDuplicateDialog, ReleaseDuplicateDialog } from './MarkDuplicateDialog';
import {
  ACTOR_ROLE_LABEL,
  AttemptSummary,
  DealActionBanner,
  DealOutcomeBlock,
  NotEligibleBanner,
  attemptThreshold,
  dealActionNeeded,
  dealOutcome,
} from './stageClarity';
import { DropOffClinicalCallout, dropOffChipFor, dropOffDrawerText } from './dropOffReasonsStaff';
import {
  ENTRY_LABEL,
  STAGE_LABEL,
  REQUIRED_ROUND_ATTEMPTS,
  TERMINAL_STAGES,
  isAwaitingClinicalDecision,
  isLeadCallCard,
  movesFor,
  stageTokenLabel,
  type ManualTarget,
} from './stages';

const TAB_ID_BASE = 'deal';

/**
 * THE OVERVIEW'S NESTED VIEW PAIR (2026-09-23) — Journey / Engagement, on the
 * SAME shared Tabs primitive as the drawer's own strip, scoped by its OWN
 * `idBase` so the two tab groups coexist on one page without their tab/panel
 * id pairs colliding (the InsightsSection precedent: a second Tabs group with
 * a second idBase).
 *
 * ⚠️ JOURNEY IS THE DEFAULT AND THE JOURNEY VIEW IS THE EXISTING JourneyPanel
 * MOUNTED BYTE-IDENTICALLY — same component, same props, same single mount.
 * The Engagement view is additive; nothing about the journey rail moved.
 */
const OVERVIEW_VIEW_ID_BASE = 'deal-overview';
/** The "Previous deals" basis line — one drawer is open at a time, so one id. */
const SIBLING_BASIS_ID = 'deal-previous-deals-basis';
type OverviewView = 'journey' | 'engagement';
const OVERVIEW_VIEW_TABS: TabDef[] = [
  { id: 'journey', label: 'Journey' },
  { id: 'engagement', label: 'Engagement' },
];

/**
 * WHO GETS THE ENGAGEMENT VIEW — ADMIN ONLY (2026-09-26, S4.5; CTO decision Q8,
 * "the per-card Engagement tab is ADMIN-only too").
 *
 * The CTO accepted the tab being visible to clinicians as an INTERIM (G-D,
 * 2026-09-26: no enrolments exist and every send is off) and asked for it to
 * close with S4.5/S4.6. This is the portal half, and it ships FIRST: clinic
 * S4.6 narrows `GET /v1/deals/:id/engagement` to `@Roles('ADMIN')` only once
 * this build is live, so no clinician's drawer is left calling a route that
 * would 403. A CLINICIAN's Overview therefore mounts the Journey panel on its
 * own — no nested tab strip, and no EngagementPanel, so the engagement read is
 * never even issued (a spec pins the request's absence, not just the tab's).
 * Deny-by-default: anything that is not exactly ADMIN is treated as clinician.
 */
export function showsEngagementView(role: UserRole): boolean {
  return role === 'ADMIN';
}

/**
 * THE KINDS A HUMAN CAN LOG — the Activity tab's INCLUDE list (2026-08-27, CTO
 * drawer review).
 *
 * MIRRORED FROM THE WRITE CONTRACT, NOT INVENTED: clinic-service
 * `src/deals/deal.schemas.ts` → `dealActivityCreate.kind` is
 * `z.enum(['note', 'call_attempt', 'email'])` — the exact and complete set of
 * kinds a person may POST to `/v1/clinic/deals/:id/activities` (the callback
 * ledger's composer, `callbackActivity`, writes the same three). So "somebody
 * sat down and logged this" is EXACTLY "this kind is in the write contract",
 * and the two can only drift if that enum changes — at which point this list is
 * the one line to change, named right here.
 *
 * WHY AN INCLUDE LIST AND NOT `kind !== 'audit'`. The old rule EXCLUDED one
 * kind, so every kind the server ever adds defaulted INTO Activity: the journey
 * milestones, the stage transitions, the system-sent emails and the clinical
 * decision beats all piled into the tab meant for staff comms, while the Audit
 * log — which is meant to be the full trail — got only the thin `audit` kind
 * and read as empty. An include list inverts that default: a new server-side
 * kind is system trail until somebody decides it is something a human logged.
 *
 * NOTE `email` vs `system_email`: the wire deliberately separates a STAFF-LOGGED
 * email (a person recording that they emailed the patient — in the write
 * contract) from a SYSTEM-SENT one (the platform's own send — not in the write
 * contract, and therefore trail). That distinction is the whole reason this
 * split can be drawn from the write contract at all.
 */
const LOGGED_KINDS: ReadonlySet<string> = new Set(['note', 'call_attempt', 'email']);

const KIND_LABEL: Record<string, string> = {
  stage_change: 'Stage',
  handover: 'Handover',
  call_attempt: 'Call',
  note: 'Note',
  email: 'Email',
  audit: 'Audit',
  // Journey-feed kinds (2026-08-26, clinic feat/journey-feed): the clinical
  // decision trail, system-sent emails (distinct from a staff-LOGGED email),
  // and the synthetic journey milestones. Unknown future kinds still degrade
  // to their raw token via this Record idiom.
  clinical_review: 'Clinical',
  system_email: 'Email sent',
  journey: 'Journey',
};

/* The actor-role chip wording (agreed clinic seam, 2026-08-27) MOVED to
 * stageClarity.tsx on 2026-08-27 and imported above: the outcome block and the
 * action banner name a role with the exact same map this feed does, and three
 * copies is three chances for one surface to drift. The full rule (why only the
 * four enum values chip, and why null/absent/unknown render nothing) lives with
 * the map — read it there before adding a value. */

/**
 * Re-render a raw stage_change summary through the human STAGE_LABEL map.
 *
 * The agreed clinic seam (2026-08-27) puts the BARE transition on the wire:
 * exactly `"<from> -> <to>"` (ASCII arrow, single spaces) with an optional
 * `" - <reason>"` tail when staff gave one. This parses that form — tolerating
 * the unicode arrow LEGACY rows carried in reason prose — and swaps only the
 * stage tokens, preserving the reason tail VERBATIM after the humanised
 * labels (the reason is staff free text; rewriting it would falsify a quote).
 * ONLY when BOTH tokens resolve to known stages; anything else (already-human
 * copy, a future stage this build predates) falls back to the raw text — a
 * wrong label would be worse than an ugly one.
 */
function humaniseStageSummary(summary: string): string {
  const match = summary.match(/^\s*([a-z_]+)\s*(?:->|→)\s*([a-z_]+)( - .*)?$/);
  if (!match) return summary;
  // `stageTokenLabel` widens the lookup twice over: STAGE_LABEL keys on
  // DealStage while the wire tokens are plain strings, AND an audit row written
  // before the 2026-09-05 restructure names a stage that is no longer in the
  // vocabulary at all. Falling back through LEGACY_STAGE_LABEL is what keeps
  // those rows in English — see the note on that map for why an append-only
  // audit trail makes this permanent rather than transitional.
  const from = stageTokenLabel(match[1]);
  const to = stageTokenLabel(match[2]);
  return from && to ? `${from} → ${to}${match[3] ?? ''}` : summary;
}

/**
 * PREVIOUS ATTEMPTS (History tab) — the same person coming back.
 *
 * A repeat journey re-points this ONE card at the newest ticket/assessment
 * instead of adding a second card to the board, so without this strip the
 * earlier attempt is invisible and the caller looks like a first-timer. The
 * brief is the FACT and the OUTCOME — when they tried, how it ended — not a
 * replay: the earlier attempt's notes and answers stay where they live.
 */
const ATTEMPT_KIND_LABEL: Record<string, string> = {
  callback: 'Callback',
  assessment: 'Eligibility check',
};

/**
 * Plain English, not the wire enum — staff scan this in the middle of a call.
 * A `Record<string, …>` (the KIND_LABEL idiom above) so an outcome added
 * server-first degrades to neutral wording instead of rendering blank.
 */
const ATTEMPT_OUTCOME_LABEL: Record<string, string> = {
  in_progress: 'Still open',
  superseded: 'They asked again before this one was closed',
  not_proceeding: 'Said they were not going ahead',
  closed: 'Closed by staff',
  parked: 'Never reached — call attempts ran out',
  not_eligible: 'Ended not eligible',
  eligible: 'Came back eligible',
  submitted: 'Submitted',
  abandoned: 'Started and never finished',
};

/** Local, NOT the canonical `toneFor` map: these are attempt endings, not
 * lifecycle states, and folding them into the shared state map would give two
 * different things one name. Unknown → neutral, same fallback rule. */
const ATTEMPT_TONES: Record<string, 'neutral' | 'positive' | 'pending' | 'muted' | 'danger'> = {
  in_progress: 'pending',
  superseded: 'pending',
  not_proceeding: 'muted',
  closed: 'muted',
  parked: 'muted',
  not_eligible: 'danger',
  eligible: 'positive',
  submitted: 'pending',
  abandoned: 'muted',
};

/**
 * One earlier attempt: what it was, how it ended, when it ran — and, since
 * S4.1 (Addendum 2), HOW it is known to be this person. The whole list is
 * correlated through the contact's EMAIL, so an email-only row carries the
 * server's own words "email match — not verified" and they are rendered
 * verbatim; an absent key (an older clinic-service) renders no basis line
 * rather than implying the match was proven.
 */
function PriorAttemptRow({ attempt }: { attempt: DealPriorAttempt }) {
  const kind = ATTEMPT_KIND_LABEL[attempt.kind] ?? attempt.kind;
  const outcome = ATTEMPT_OUTCOME_LABEL[attempt.outcome] ?? attempt.outcome.replace(/_/g, ' ');
  const matchLabel = attempt.matchLabel?.trim()
    ? attempt.matchLabel.trim()
    : attempt.matchBasis && attempt.matchBasis !== 'email_unverified'
      ? MATCH_BASIS_LABEL[attempt.matchBasis]
      : null;
  return (
    <li style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
      <span>
        <Badge tone={ATTEMPT_TONES[attempt.outcome] ?? 'neutral'}>{kind}</Badge>
        <span style={{ marginLeft: '8px', fontSize: '13px', color: 'var(--gps-text)' }}>{outcome}</span>
        {matchLabel ? (
          <span
            data-match-label
            style={{
              display: 'block',
              marginTop: '4px',
              fontSize: '12.5px',
              fontWeight: 600,
              color: attempt.matchBasis === 'email_unverified' ? 'var(--gps-warn-text)' : 'var(--gps-muted)',
            }}
          >
            {matchLabel}
          </span>
        ) : null}
      </span>
      <span style={{ fontSize: '13px', color: 'var(--gps-muted)', whiteSpace: 'nowrap' }}>
        {formatUtcDate(attempt.startedAt)}
        {/* The ending only earns its place when it is a DIFFERENT day — a
            same-day open-and-close would otherwise print the date twice. */}
        {attempt.endedAt && formatUtcDate(attempt.endedAt) !== formatUtcDate(attempt.startedAt)
          ? ` → ${formatUtcDate(attempt.endedAt)}`
          : ''}
      </span>
    </li>
  );
}

/** The CTA wording per move channel — the "More moves" menu rows keep the
 * channel's own verb (Convert / Hand over / Close as lost) so the side-effect
 * each dialog runs stays legible. */
function moveLabel(move: ManualTarget): string {
  if (move.via === 'convert') return 'Convert to callback';
  if (move.via === 'handover') return move.to === 'awaiting_admin_followup' ? 'Hand over to admin' : 'Hand back to clinician';
  return move.to === 'closed_lost' ? 'Close as lost' : `Move to ${STAGE_LABEL[move.to]}`;
}

/** One feed row — shared by the Activity feed and the Audit-log panel so the
 * audit panel reuses the exact same rendering (and sanitisation) rules.
 * 2026-08-26 §1 (feed clarity): stage_change summaries re-render through the
 * STAGE_LABEL map, the timestamp gains its TIME (formatUtcDateTime — two
 * same-day entries were indistinguishable before), and each row names WHO:
 * the actor-role chip (the four seam roles only — see ACTOR_ROLE_LABEL), plus
 * — for an ADMIN viewer — the resolved display name (`actorName`, from the
 * drawer's one batched lookup; the raw role chip stays the fallback and the
 * whole CLINICIAN experience, which issues no lookup at all).
 *
 * 2026-08-27 (CTO audit-log review) — READING ORDER. The row read
 * `[kind] [summary] … [role] [name] [timestamp]`: the two facts a reader of an
 * audit trail scans FIRST — WHEN, and WHO — sat at the far right of a
 * variable-width line, AFTER the account of the act they are meant to
 * attribute, and their left edge moved with every summary's length so they
 * could not be read down as a column. The row now reads left to right:
 *
 *     [timestamp] [ROLE] [name] [kind] [summary]
 *
 * "type of the user" (the CTO's words) is the ACTOR ROLE, not the entry kind.
 * The kind chip stays welded to the summary it categorises, because "Stage"
 * and "Clinical review → Won" are one statement of what was done — separating
 * them would leave the summary unlabelled and the chip unattached.
 *
 * THE ROLE CHIP IS NOW LABELLED AS A ROLE (`role="img"` + aria-label + title —
 * the EntryPathChip idiom). A bare "ADMIN" standing in the slot where a
 * person's name goes is precisely how a system-driven stage move came to be
 * read as an act by an administrator. The VISIBLE chip stays the short word so
 * the column still scans; the accessible name and the tooltip say "Role:
 * Admin", so neither a screen-reader user nor anyone hovering can mistake the
 * class of actor for the actor.
 *
 * NO NAME IS INVENTED. Display names never travel on this wire (R19 — audit
 * rows are PHI-free, opaque ids only; clinic-service resolves no actor names
 * server-side by design), so the name slot is filled ONLY by the ADMIN
 * viewer's batched staff lookup. A CLINICIAN viewer, a PATIENT or SYSTEM
 * actor, and any id the staff directory does not know all render the role
 * alone — never a raw UUID (which reads to staff as "no audit recorded") and
 * never a guess. */
/**
 * The feed kinds a PERSON authored — the three `POST /v1/deals/:id/activities`
 * accepts (`dealActivityCreate`: note | call_attempt | email). Only these get
 * the "Posted …" byline.
 *
 * WHY NOT EVERY ROW. The feed is MERGED: it also carries `stage_change`,
 * `handover`, `audit`, `clinical_review`, `system_email` and synthetic
 * `journey` milestones the server derives from row timestamps. "Posted" names
 * a person publishing something, so putting it on a derived milestone or an
 * automated email would assert an author that does not exist — the same class
 * of mistake as the bare ADMIN chip that made a system-driven stage move read
 * as an act by an administrator. Those rows keep the bare stamp.
 *
 * NO EDIT CLAUSE IS POSSIBLE HERE, and that is a property of the data, not an
 * omission: `DealNote` has `createdAt` only (no `editedAt`/`updatedAt`) and the
 * controller exposes no PATCH or DELETE for an activity, so an activity item
 * is append-only. `noteByline` is still the right helper — it renders
 * "Posted <when>" when `editedAt` is absent, which keeps one implementation of
 * the wording shared with the clinical panels instead of a second copy that
 * can drift. If activities ever become editable, pass `editedAt` and the edit
 * clause appears with no further change here.
 */
const AUTHORED_ACTIVITY_KINDS: ReadonlySet<DealActivityEntry['kind']> = new Set(['note', 'call_attempt', 'email']);

/** A staff actor resolved through the ADMIN-only batched identity lookup. */
export interface ResolvedActor {
  /** `fullName` when the account has one, else the email standing in for it. */
  name: string;
  email: string;
  /**
   * TRUE only when `name` is a real stored full name — NOT an email standing in
   * for one. This is the gate on dropping the role tag, and it is the whole
   * reason the flag exists rather than a `name !== email` check at the call
   * site.
   *
   * ✅ THE WIRE NOW CARRIES THE NAME (identity shipped it 2026-09-06). This
   * block used to say the opposite, and the history is worth keeping because it
   * is why the flag is shaped the way it is: `staffUserLookupResponse` was
   * `.strict()` over `{ userId, email }` and `AdminService.lookupUsers` selected
   * only id+email, so `fullName` was not merely omitted — it was
   * unrepresentable, and every email in the audit log was every account, not a
   * handful of legacy ones.
   *
   * Gating the tag-drop on a REAL name is what made this safe to ship BEFORE
   * the wire existed, and it is why nothing had to change in the portal when
   * identity added its two lines: rows with a stored name now gain it and give
   * up the role tag on their own. Rows without one — the seeded master admin,
   * which genuinely has no name in any environment — correctly keep the tag and
   * show the address. Gating on "an actor resolved" instead would strip
   * the role tag from every row TODAY and leave a bare email where the actor
   * column used to say who this was.
   */
  hasRealName: boolean;
}

/**
 * The roles that belong to a PERSON with a staff account behind them — the
 * only rows whose role chip may be dropped in favour of a name.
 *
 * ⚠️ PATIENT AND SYSTEM ARE DELIBERATELY NOT HERE. Neither carries a name or an
 * email on this wire, so for those rows the role chip is the ONLY actor
 * indicator there is. Dropping it would leave "who moved this deal" unanswerable
 * on exactly the rows where a reader is most likely to be asking — a
 * system-driven stage move with no chip reads as an act by nobody. The entire
 * point of an audit log is that every row can name its class of actor.
 */
const STAFF_ACTOR_ROLES: ReadonlySet<string> = new Set(['CLINICIAN', 'ADMIN']);

/**
 * The actor's NAME, with their email and role one hover or one click away.
 *
 * WHY A REAL BUTTON AND NOT JUST `title`. A tooltip is a mouse affordance: it
 * is unreachable on touch, and a keyboard user tabbing an audit trail never
 * sees it. `title` alone would mean the email and role — the disambiguating
 * facts, the ones that matter when two staff share a first name — were
 * available only to people using a mouse. So the name is a real `<button>`
 * with `aria-expanded`, and clicking or pressing it reveals the detail inline;
 * `title` and `aria-label` carry the same sentence for hover and for assistive
 * tech, so all three routes give identical information.
 *
 * The button is styled as text on purpose. This is a dense, scannable log and a
 * column of chrome-heavy controls would drown the content — the name must still
 * read as a name. It keeps a focus outline (never `outline: none`) so the
 * keyboard route stays visible.
 */
function ActorName({ actor, roleLabel }: { actor: ResolvedActor; roleLabel: string | null }) {
  const [revealed, setRevealed] = useState(false);
  // The email is worth showing even when it is ALSO the name (a legacy
  // name-less account): repeating it is honest and costs a reader nothing,
  // whereas suppressing it would make the reveal look broken on exactly the
  // rows where the name is the weakest.
  const detail = [actor.email, roleLabel].filter((part): part is string => Boolean(part)).join(' · ');
  const full = `${actor.name} — ${detail}`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setRevealed((open) => !open)}
        aria-expanded={revealed}
        aria-label={full}
        title={full}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'var(--gps-text)',
          cursor: 'pointer',
          textDecoration: 'underline dotted',
          textUnderlineOffset: '3px',
        }}
      >
        {actor.name}
      </button>
      {revealed ? <span style={{ color: 'var(--gps-muted)' }}>{detail}</span> : null}
    </span>
  );
}

/**
 * The counterpart deal a duplicate TRAIL row may link to, resolved once by the
 * drawer and handed down. Two directions, because the two audit actions sit on
 * opposite cards — see `absorbedRowLink` for why the canonical side is allowed
 * to be null while the duplicate side never is.
 */
export interface DuplicateTrailLinks {
  /** The canonical, on a card that was merged away. */
  marked: string | null;
  /** The one merged duplicate, when the canonical's trail is unambiguous. */
  absorbed: string | null;
}

function ActivityRow({
  entry,
  actor,
  duplicateLinks,
  onOpenDeal,
}: {
  entry: DealActivityEntry;
  actor?: ResolvedActor;
  duplicateLinks?: DuplicateTrailLinks;
  onOpenDeal?: (dealId: string) => void;
}) {
  /**
   * (d) BOTH TRAILS CARRY THE LINK (2026-09-06). clinic-service writes
   * `deal_marked_duplicate` on the duplicate and `deal_duplicate_absorbed` on
   * the canonical, and gives neither a feed label — so both arrive here as the
   * generic "de-underscored action — reason" text and read as machine output in
   * the middle of an audit trail. `duplicateTrailRow` recognises them off that
   * action prefix (see the long note in duplicateSurface.tsx for why that is the
   * available handle and what the two-line server fix would be) and an
   * unrecognised row falls straight through to the existing rendering.
   */
  const duplicateRow = duplicateTrailRow(entry.summary);
  // null AND undefined both mean "role not recorded" (note ledgers, ambiguous
  // milestones, an older server): render NO chip. Only the four seam enum
  // values chip — see the ACTOR_ROLE_LABEL note for why nothing is guessed.
  const roleLabel = entry.actorRole ? (ACTOR_ROLE_LABEL[entry.actorRole] ?? null) : null;
  /**
   * 2026-09-05 — THE LOG LEADS WITH THE NAME, AND THE ROLE TAG GOES, BUT ONLY
   * WHERE A NAME ACTUALLY REPLACES IT.
   *
   * `createInvite` has required a full name for both staff roles since
   * 2026-07-16 ("no name-less staff account can ever be minted at accept"), so
   * essentially every staff account has one. Leading with the name is therefore
   * the normal case, not the lucky one.
   *
   * ⚠️ This used to add that "the emails that used to fill this column are
   * legacy accounts predating that rule" — which was WRONG, and being wrong
   * about it is what let the real cause sit undiagnosed for months: the name
   * could not cross the identity boundary at all, so EVERY row showed an email,
   * not a handful of old ones. See `ResolvedActor.hasRealName` above.
   *
   * ALL THREE CONJUNCTS ARE LOAD-BEARING:
   *  - a STAFF role, so PATIENT and SYSTEM rows keep the only actor indicator
   *    they have (see STAFF_ACTOR_ROLES);
   *  - a RESOLVED actor, so a row whose lookup failed, or a CLINICIAN viewer
   *    (who issues no lookup at all and therefore resolves nobody), keeps the
   *    role chip rather than losing its actor column entirely. Dropping the
   *    chip on the strength of the role ALONE would blank the actor on exactly
   *    the rows the identity batch could not answer for;
   *  - a REAL NAME, not an email standing in for one — see `hasRealName`. An
   *    email is not the thing that replaces the role tag. (This clause used to
   *    end "and until identity sends `fullName` an email is all this lookup can
   *    return"; identity shipped the field on 2026-09-06, so named rows now take
   *    the name path and only genuinely name-less accounts keep the tag.)
   */
  const namedStaff = actor?.hasRealName === true && entry.actorRole != null && STAFF_ACTOR_ROLES.has(entry.actorRole);
  const showRoleChip = roleLabel != null && !namedStaff;
  return (
    <li style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '8px 12px' }}>
      {/* `flexWrap` replaces the old `justifyContent: 'space-between'`: the meta
          used to be pinned right, so it could never collide with the summary.
          Now that when/who/what read on ONE left-to-right line, a long summary
          has to wrap the row rather than squeeze the fixed-width prefix — hence
          the nowrap stamp and the summary group's own `minWidth: 0`. */}
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--gps-muted)' }}>
        <span style={{ whiteSpace: 'nowrap' }}>
          {AUTHORED_ACTIVITY_KINDS.has(entry.kind) ? noteByline({ createdAt: entry.at }) : formatUtcDateTime(entry.at)}
        </span>
        {showRoleChip ? (
          <span role="img" aria-label={`Role: ${roleLabel}`} title={`Role: ${roleLabel}`}>
            <Badge tone="muted">{roleLabel}</Badge>
          </span>
        ) : null}
        {/* The PERSON, when one is resolvable — text-coloured, because the name
            is content, while the stamp and the chips are chrome. */}
        {actor ? (
          // A real name hides two facts worth revealing (the email and the
          // role), so it gets the disclosure. An email-fallback row is already
          // showing its email beside a role chip — there is nothing behind the
          // click, so it stays plain text rather than offering an empty
          // control on every row of the log.
          actor.hasRealName ? (
            <ActorName actor={actor} roleLabel={roleLabel} />
          ) : (
            <span style={{ color: 'var(--gps-text)' }}>{actor.name}</span>
          )
        ) : null}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Badge tone={entry.kind === 'call_attempt' && entry.outcome !== 'answered' ? 'pending' : 'neutral'}>
            {KIND_LABEL[entry.kind] ?? entry.kind}
          </Badge>
          <span style={{ color: 'var(--gps-text)' }}>
            {duplicateRow ? (
              <DuplicateTrailSummary
                row={duplicateRow}
                linkDealId={
                  duplicateRow.kind === 'marked'
                    ? (duplicateLinks?.marked ?? null)
                    : duplicateRow.kind === 'absorbed'
                      ? (duplicateLinks?.absorbed ?? null)
                      : null
                }
                onOpenDeal={onOpenDeal}
              />
            ) : entry.kind === 'stage_change' ? (
              humaniseStageSummary(entry.summary)
            ) : (
              entry.summary
            )}
          </span>
        </span>
      </span>
      {entry.body ? (
        // Defence in depth: server-sanitised, stripped AGAIN here.
        <div
          style={{ fontSize: '13.5px', marginTop: '4px', color: 'var(--gps-text)' }}
          dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(entry.body) }}
        />
      ) : null}
      {entry.attachments && entry.attachments.length > 0 ? (
        // Wave 4 §4: note attachments as chips — the signed read URL links out
        // in a new tab; a null url (stub-mode storage) renders a plain "stored"
        // chip instead of a dead link. Chips are DATA-built, never note HTML.
        <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
          {entry.attachments.map((attachment) => {
            const name = attachment.ref.split('/').pop() || attachment.ref;
            const chipStyle: React.CSSProperties = {
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-r-sm)',
              padding: '3px 8px',
              fontSize: '12.5px',
              background: 'var(--gps-surface-2)',
              color: 'var(--gps-text)',
            };
            return attachment.url ? (
              <a
                key={attachment.ref}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...chipStyle, color: 'var(--gps-link)', textDecoration: 'none' }}
              >
                📎 {name}
              </a>
            ) : (
              <span key={attachment.ref} style={chipStyle}>
                📎 {name} · stored — no preview on this environment
              </span>
            );
          })}
        </span>
      ) : null}
    </li>
  );
}

export function DealDrawer({
  card: listCard,
  initialTab = 'overview',
  onClose,
  onChanged,
  onRequestMove,
  onOpenDeal,
}: {
  /** The clicked board card — renders the frozen header before the detail lands. */
  card: DealCard;
  /** Land on a specific tab — Create-new-lead opens straight into the questionnaire. */
  initialTab?: string;
  onClose: () => void;
  onChanged: () => void;
  /** Route a legal manual move to its owning dialog (PipelineTab). */
  onRequestMove: (card: DealCard, target: ManualTarget) => void;
  /**
   * OPEN ANOTHER DEAL BY ID (2026-09-06) — how the duplicate pair is reachable
   * from either end.
   *
   * ⚠️ BY ID, NOT BY CARD, AND THAT IS THE WHOLE POINT. Every other route into
   * this drawer hands over a `DealCard` the board already holds, and a merged
   * duplicate is exactly the card the board does NOT hold: clinic-service hides
   * duplicates from the working lanes, so the counterpart is usually not in the
   * loaded page. The owner (PipelineTab) resolves the id — from the page if it
   * is there, otherwise by fetching the detail — which is the only way the
   * pointer can actually be followed.
   *
   * OPTIONAL: absent simply renders the sentences without their links, so a
   * caller that has no way to navigate is not forced to invent one.
   *
   * MAY REJECT, AND THE REJECTION IS THIS COMPONENT'S TO REPORT. The owner
   * fetches a card the board does not hold, so the click can fail — the
   * canonical was purged, or the network dropped. It is reported HERE, inside
   * the dialog, because that is the only place the person who clicked is
   * looking: the drawer is `aria-modal`, so anything the owner renders behind
   * it is both covered and outside the accessibility tree. See `openLinkedDeal`.
   */
  onOpenDeal?: (dealId: string) => void | Promise<void>;
}) {
  const { me } = useAuth();
  const role: UserRole = me?.role ?? 'CLINICIAN';
  const [detail, setDetail] = useState<DealDetail | null>(null);
  /**
   * A FOLLOW-THE-DUPLICATE LINK THAT FAILED, reported where the reader is.
   *
   * ⚠️ WHY THIS IS NOT THE OWNER'S JOB, even though the owner does the fetch.
   * PipelineTab's notice lane renders on the BOARD, and every caller of
   * `onOpenDeal` is inside this drawer — a fixed, full-viewport scrim wrapping
   * a `role="dialog" aria-modal="true"`. An alert on the board is therefore
   * painted under the scrim AND pruned from the accessibility tree by
   * `aria-modal`, so a screen-reader user hears nothing and a narrow viewport
   * shows nothing. The apology existed and could not be received, which reads
   * to the user as the broken link it was written to prevent.
   */
  const [linkError, setLinkError] = useState<string | null>(null);
  const linkErrorRef = useRef<HTMLDivElement | null>(null);
  // 'gone' = 404 after a CLIN-04 under-13 purge (or any hard-deleted deal):
  // Retry would loop; Close is the only honest action.
  const [status, setStatus] = useState<'loading' | 'ready' | 'error' | 'gone'>('loading');
  const [tab, setTab] = useState(initialTab);
  /**
   * The History tab's own route (S4.2 / S4.5), read the first time the tab is
   * opened and held here so a reveal's answers survive a tab switch. Dark on
   * every environment today (404 `disabled`) — see HistoryPanel.tsx.
   */
  const history = useDealHistory(listCard.dealId, tab === 'history');
  // The Overview tab's nested Journey / Engagement view (2026-09-23). Journey
  // is the default per open, exactly as the drawer's own tab state is — a
  // controlled Tabs owner holds the active id (the primitive is state-free).
  const [overviewView, setOverviewView] = useState<OverviewView>('journey');
  const [reviewOrder, setReviewOrder] = useState<StaffOrder | null | 'missing'>(null);
  // Recover-lead (hard-stop safety net undo): the confirm dialog + its async state.
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  // Send-for-admin-review (the clinician→admin handoff): the SAME dialog shape.
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  // Mark-as-test-record (reporting hygiene): the SAME dialog shape again.
  const [markTestOpen, setMarkTestOpen] = useState(false);
  const [markTestBusy, setMarkTestBusy] = useState(false);
  const [markTestError, setMarkTestError] = useState<string | null>(null);
  // TRUE once the ORDER half of the marking landed. Two services are involved
  // (orders-service case + clinic-service deal, fired in THAT order — see
  // runMarkTest for why), so a retry after a partial failure must re-fire
  // ONLY the deal half — re-posting the landed order half would write a
  // second audit row for one human action.
  const [markTestOrderDone, setMarkTestOrderDone] = useState(false);
  /**
   * WHICH ORDER CASE THIS MARK HAS TO FLAG — resolved when the dialog OPENS.
   *
   * ⚠️ THIS EXISTS BECAUSE `deal.orderCaseRef` IS NOT RELIABLY SET, AND THE OLD
   * `if (deal.orderCaseRef)` GUARD TURNED THAT INTO SILENT LIVE-LOOKING REVENUE.
   *
   * clinic-service writes `Deal.orderCaseRef` from ONE place: the order-sync
   * ingress, which finds the deal by `assessmentId` and then bails on
   * `!isAutoMovable(deal.stage)`. So the ref is missing whenever the deal was
   * TERMINAL or PARKED when checkout landed (`closed_won` / `closed_lost` /
   * `pending_admin_review` / `awaiting_admin_followup`), or whenever the deal is
   * not linked to the assessment the order was built from. The order exists; the
   * card just does not know its name. Marking the deal then closed and HID it
   * while its order stayed "real" on the admin money boards — which is exactly
   * the three `DORMANT` / awaiting-first-payment cards at £565.95 the CTO is
   * looking at.
   *
   * WHAT THIS DOES: falls back to the ASSESSMENT-KEYED lane. The staff queue
   * (`GET /v1/orders`) carries `assessmentRef` on every item, so a deal with an
   * `assessmentId` can find its own order without the ref. It is the same read
   * this drawer already makes for the Review tab, so no new surface and no new
   * shape.
   *
   * WHAT IT DELIBERATELY DOES NOT CLAIM: that lane is BOUNDED (orders pages the
   * queue newest-first at MAX_QUEUE_PAGE) and ROLE-FILTERED (a clinician's queue
   * is an allow-list of live clinical-decision states), so an empty answer is
   * "we could not find one", never "there is none". `'none'` is therefore
   * rendered as exactly that, with the ADMIN Payments-board mark named as the
   * way to finish the job — see the dialog body. Paging the entire queue to be
   * sure is the anti-pattern orders-service already removed when it added
   * `GET /case/:id`, and it still would not cover the clinician's state filter.
   *
   * WHAT WAS REJECTED, and why the honest fix is not here:
   *  - MAKE THE DEAL CARRY ITS REF. The real root cause, and a clinic-service
   *    change (write `orderCaseRef` even when the stage will not move — the
   *    linkage is not a stage transition and should never have been carried by
   *    one). Recorded as a follow-up; it makes this fallback dead code, which is
   *    the right end state.
   *  - ONE SERVER-SIDE ACTION MARKING BOTH. The right long-term shape, but the
   *    two flags live in two services and no transaction spans them — it is a
   *    saga, not a refactor, and it would replace a documented, retryable
   *    partial-failure story with an undocumented one.
   *  - RESOLVING FROM THE PATIENT. The board's other identity is the contact
   *    email; a contact can own several deals and several orders, so a
   *    portal-side join by person could mark the WRONG case.
   *
   * `null` = the dialog is closed. Resolution is deliberately at OPEN time, not
   * at confirm time: the copy has to be true BEFORE the operator presses it.
   */
  const [markOrderLink, setMarkOrderLink] = useState<
    { state: 'checking' } | { state: 'found'; orderCaseId: string } | { state: 'none' } | null
  >(null);
  // Unmark-test-record (2026-08-26 §5 — the mark's undo): the SAME dialog
  // shape. DEAL-ONLY on purpose: the server's revert flag (clinic#137)
  // clears the deal's flag ONLY — the stage stays put, and the orders half
  // keeps its own truth (an admin un-marks a case from the money boards if
  // one was flagged).
  const [unmarkOpen, setUnmarkOpen] = useState(false);
  const [unmarkBusy, setUnmarkBusy] = useState(false);
  const [unmarkError, setUnmarkError] = useState<string | null>(null);
  /* ── THE DUPLICATE ACTION (2026-09-06) ─────────────────────────────────────
   * `dupMode` is the open dialog: 'mark' (pick a canonical) or 'release' (the
   * undo). One piece of state rather than two booleans, because the two are
   * mutually exclusive by construction — a card is either merged or it is not —
   * and two flags would admit a state where both dialogs are mounted.
   *
   * `dupPayment` holds the response's payment block AFTER the action lands, so
   * the surface can tell the operator to go and look at the money. It is
   * deliberately not cleared by a reload: the charge is still there.
   */
  const [dupMode, setDupMode] = useState<'mark' | 'release' | null>(null);
  /* ── REOPEN A SUPERSEDED CARD (2026-09-26, S4.5 — clinic S4.1's ADMIN route) ──
   * The ONE undo for a card the SYSTEM closed because a newer questionnaire
   * arrived. Until this build nothing called `POST …/reopen-superseded`, and
   * the drawer told staff to "un-mark the duplicate" — the wrong undo, which the
   * server refuses on a superseded card. REQUIRED reason (3..2000). */
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [reopenNotice, setReopenNotice] = useState<string | null>(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupError, setDupError] = useState<string | null>(null);
  const [dupPayment, setDupPayment] = useState<{ reviewRequired: boolean; orderCaseRef: string | null } | null>(
    null,
  );
  /**
   * WHAT THE CASCADE DID, HELD UNTIL THE OPERATOR HAS READ IT (2026-09-06).
   *
   * `POST mark-test` answers `{deal, cascade}` — the deal, the plan behind it
   * and the patient behind it — and this drawer used to discard the body. A
   * WITHHELD patient leg (the clinical-safety rule firing), a FAILED plan leg
   * or an ANOMALY therefore rendered as an unqualified success on a record that
   * was only part-marked. See pipeline/markTestCascade for the whole argument.
   *
   * `reason` is kept ALONGSIDE the cascade because the re-run has to repeat the
   * IDENTICAL request: the server finishes an incomplete cascade by idempotent
   * re-post, and dropping the operator's audit note on the second attempt would
   * leave two rows for one human action saying different things.
   */
  const [cascadeReport, setCascadeReport] = useState<
    { direction: CascadeDirection; cascade: DealTestCascade; reason?: string } | null
  >(null);
  const [cascadeBusy, setCascadeBusy] = useState(false);
  const [cascadeError, setCascadeError] = useState<string | null>(null);
  // ADMIN-only actor display names for the Activity/Audit feeds (§1) — the
  // SAME one-batched-lookup pattern as admin/AuditLogTab.tsx: `lookedUp`
  // remembers ids already ATTEMPTED, `actorDirectory` holds the resolved
  // name+email map. CLINICIAN viewers never populate either — the role chip is their
  // whole actor display.
  //
  // 2026-08-28: "attempted" now means attempted, not "resolved" — a failed
  // batch no longer un-marks its ids. See the effect below for why the old
  // clear-and-retry was unsafe once the effect gained a dependency array.
  // 2026-09-05: the map holds the NAME **and** the email, not a single
  // pre-collapsed display string. The audit log now leads with the name and
  // reveals the email on demand, so both halves have to survive the lookup —
  // collapsing them at resolution time is what forced the old row to show
  // whichever one it happened to get.
  const [actorDirectory, setActorDirectory] = useState<Record<string, ResolvedActor>>({});
  const lookedUp = useRef(new Set<string>());
  // Last successful review action — kept HERE so "Prescription issued."
  // survives the detail reload that follows approve (the deal moves to Won
  // and ReviewView would otherwise remount with an empty notice).
  const [actionNotice, setActionNotice] = useState<ActionNotice | null>(null);
  // The footer's "Log activity" composer MODAL (2026-08-27 restructure — the
  // composer left the Activity tab; the tab is the comms trail only).
  const [logOpen, setLogOpen] = useState(false);
  /**
   * The warm-lead calling lane (2026-09-08, CTO) — a SECONDARY surface. It has
   * no board column and no toolbar chip; the one way in is the ⋯ menu row
   * below, which is what "it won't be a primary workflow" looks like in a UI.
   */
  const [leadCallOpen, setLeadCallOpen] = useState(false);

  const load = useCallback(() => {
    setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    getDealDetail(listCard.dealId)
      .then((d) => {
        setDetail(d);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        // CLIN-04 — answering under-13 erases the lead + deal. The hard-stop
        // refresh then 404s; surface that as a closed/not-eligible state, never
        // the generic "could not load" + Try again loop.
        setStatus(err instanceof ApiError && err.status === 404 ? 'gone' : 'error');
      });
  }, [listCard.dealId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * FOLLOW A LINK TO ANOTHER DEAL — the one thing every duplicate-pair link
   * calls, so the failure is handled ONCE rather than at each of the three
   * sites that render one.
   *
   * On success the owner swaps this drawer's card and nothing here has to
   * unwind: the component stays mounted with a new `card`, and the error is
   * already cleared at the top of the attempt.
   *
   * WHAT MAKES THE FAILURE RECEIVABLE, since the point of this whole change is
   * that the previous message was not:
   *  - it renders INSIDE the dialog, so `aria-modal` cannot prune it and the
   *    scrim cannot cover it;
   *  - `role="alert"`, so a screen reader announces it without the user having
   *    to go looking;
   *  - and it is scrolled into view, because the links that trigger it live
   *    well down the Overview panel and in the Activity feed — an alert pinned
   *    above the tabs is off-screen from exactly where the click happened.
   */
  const openLinkedDeal = useCallback(
    (dealId: string) => {
      setLinkError(null);
      // `Promise.resolve` so a caller that returns plain `void` (the prop is
      // deliberately tolerant of both) is not a runtime error here.
      Promise.resolve(onOpenDeal?.(dealId)).catch(() => {
        setLinkError('Sorry — we could not open that deal. It may have been removed.');
      });
    },
    [onOpenDeal],
  );

  // Scroll the alert into view only when one APPEARS — keyed on the message so
  // a re-render for any other reason does not yank the panel around.
  //
  // `?.` ON THE METHOD, not just the ref: jsdom does not implement
  // `Element.prototype.scrollIntoView`, so calling it unguarded throws inside
  // the effect and React unmounts the whole drawer — the alert this exists to
  // reveal disappears along with it. Progressive enhancement either way: the
  // `role="alert"` announcement and the in-dialog placement are the guarantees,
  // and the scroll is the sighted-user convenience on top.
  useEffect(() => {
    if (linkError) linkErrorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [linkError]);

  const deal = detail?.deal ?? listCard;

  // Review AND Payments both need the StaffOrder behind orderCaseRef
  // (ReviewView's input / the admin money block) — fetched once per drawer.
  //
  // 2026-08-07: this used to wait for the Review/Payments tab to be opened. It
  // can't any more — the order carries the `patientUserId` the key-alerts check
  // is keyed on, and the whole point of the tab warning is that it is up BEFORE
  // the clinician opens the tab. One extra staff-queue read per order-backed
  // drawer is the price of not hiding a double-prescribing alert behind a click.
  useEffect(() => {
    if (!deal.orderCaseRef || reviewOrder) return;
    getStaffQueue()
      .then((orders) => setReviewOrder(orders.find((o) => o.orderCaseId === deal.orderCaseRef) ?? 'missing'))
      .catch(() => setReviewOrder('missing'));
  }, [deal.orderCaseRef, reviewOrder]);

  // KEY ALERTS (2026-08-07 Phase 2) — hoisted to the DRAWER so one read feeds
  // both the Review tab's warning marker and the band inside the tab. Keyed on
  // the order's verified patient, excluding the case under review so it never
  // alerts about itself; `null` until the order lands, which issues no request.
  const safetyOrder = reviewOrder && reviewOrder !== 'missing' ? reviewOrder : null;
  const safety = useMedicationSafety(safetyOrder?.patientUserId ?? null, safetyOrder?.orderCaseId);
  // Null when there is nothing to flag. A failed check IS flagged, with its own
  // wording — fail loud on the display (see keyAlertFlag).
  const safetyFlag = keyAlertFlag(safety.view);
  // "An earlier check for this journey ended not eligible" — from the deal
  // detail, which already loads the assessment row, so the marker is up BEFORE
  // the Review tab mounts and fetches the review. A clinician who never opens
  // the tab still knows.
  //
  // PRECEDENCE IS DELIBERATE AND ONE-WAY: `TabDef.warning` is a single string
  // and the tab renders exactly one marker, so these must collapse. A
  // PRESCRIBING-SAFETY signal must never be masked by an edit marker — the
  // medication key alert always wins, and this shows only when there is no
  // safety flag to show.
  const supersedesFlag = detail?.assessment?.supersedes
    ? 'Answers changed after an earlier check'
    : null;
  /**
   * WHAT THIS ORDER STILL OWES A PRESCRIBER (2026-09-12) — read off the staff
   * order the drawer has already resolved, so it costs no extra request.
   *
   * `null` until the order lands, which flags nothing: a card whose order has
   * not arrived must not claim a prescription is missing.
   */
  const prescribingFlags = prescribingFlagsOf(safetyOrder);

  /**
   * PRECEDENCE, AND IT IS ONE-WAY. `TabDef.warning` is a single string and the
   * tab renders exactly one marker, so these have to collapse:
   *
   *   1. the medication KEY ALERT — a prescribing-SAFETY signal (are we about
   *      to duplicate somebody's medicine?). It always wins.
   *   2. the prescribing DEBT — a paid patient with no prescription, or one
   *      whose prescription records no strength. Work that is owed.
   *   3. the answers-changed marker — informational.
   *
   * 2 sits above 3 because it is the only one of the three with a patient
   * waiting on the other end of it.
   */
  const reviewTabWarning = safetyFlag ?? prescribingTabWarning(prescribingFlags) ?? supersedesFlag;

  // The Questionnaire tab: whenever an assessment exists (read-only for
  // admins), or for a CLINICIAN who can still start one on an open deal
  // (Clinical-review deals carry the questionnaire in the Review tab).
  const showQuestionnaire =
    detail?.assessment != null ||
    // The last clause was `deal.stage !== 'awaiting_clinical_review'` until the
    // 2026-09-05 merge: a card in the clinical REVIEW queue carries its
    // questionnaire in the Review tab, so offering "start one" beside it is a
    // second, competing entry point to the same assessment. That is true of the
    // AUTHORISED cards specifically — the ones a held card put in the queue —
    // and not of the unauthorised half of the merged lane, which never had the
    // restriction and is precisely where a clinician does start a questionnaire.
    (role === 'CLINICIAN' && !TERMINAL_STAGES.includes(deal.stage) && !isAwaitingClinicalDecision(deal));

  // UX Wave 4 §4 tab order: Overview · Questionnaire · Activity · History,
  // with the conditional Review/Payments tabs slotted before Activity.
  // UX Wave 9 §1: Audit log is the LAST tab, floated right — same content
  // area as every other tab, visually apart as governance chrome.
  // Item 2 (2026-07-28): the full clinical Review is available for ANY submitted
  // assessment — not only once an order case exists. Decision actions inside stay
  // order-gated (ReviewView renders them only with an order). A draft has nothing
  // to review yet, so require a non-draft assessment state.
  const reviewableAssessment =
    !!deal.assessmentId && deal.assessmentState != null && deal.assessmentState !== 'draft';
  const showReviewTab = !!deal.orderCaseRef || reviewableAssessment;

  // Stage clarity (2026-08-27) — the two server facts this drawer now leads
  // with. Both live on the DETAIL aggregate (the board list carries only the
  // compact halves), so both are null until the detail lands, and null unless
  // the server actually said so: see dealOutcome/dealActionNeeded for the
  // closed_lost + something-to-say gates and the `required === true` rule.
  const outcome = dealOutcome(deal, detail?.outcome);
  const actionNeeded = dealActionNeeded(detail?.actionNeeded);

  /* ── THE DUPLICATE PAIR (2026-09-06, CTO — the Asana model) ───────────────
   *
   * Four server facts, read once here and spent across the header, the banners
   * above the tabs, the Overview list and both activity trails. Every one is an
   * explicit `!= null` / `=== true` inside duplicateSurface.tsx: a
   * clinic-service that predates the feature omits the keys, and ABSENT means
   * "this server does not report duplicates", never "this card is not one".
   */
  const mergedInto = isMergedDuplicate(deal);
  /** The SYSTEM closed this card for a newer questionnaire (S4.1) — a different fact from `mergedInto`. */
  const superseded = isSuperseded(deal);
  /** Oldest merge first — the server's order, and the order the list renders. */
  const mergedDuplicates = detail?.mergedDuplicates ?? [];
  /** ⚠️ The merged rows that carry a charge — the money rule's whole surface. */
  const paidDuplicates = paidMergedDuplicates(mergedDuplicates);
  /**
   * The counterpart each trail row may link to. `marked` is trivially the one
   * canonical this card points at; `absorbed` is refused unless the pair is
   * unambiguous — see `absorbedRowLink` for the revert case that makes a naive
   * "link it to the only merged duplicate" put a WRONG deal on a clinical
   * record.
   */
  const duplicateTrailLinks = {
    marked: deal.duplicateOfDealId ?? null,
    absorbed: absorbedRowLink({
      mergedDealIds: mergedDuplicates.map((row) => row.dealId),
      absorbRowCount: countAbsorbRows(detail?.activity ?? []),
    }),
  };

  /* ── TAB MARKERS (2026-09-06) ─────────────────────────────────────────────
   *
   * What is waiting inside a tab, said BEFORE anybody opens it. Three shapes,
   * three meanings — see `TabMarker` in ui/Tabs.tsx for why the severe one had
   * to stop being the only one available.
   *
   * ⚠️ EVERY MARKER BELOW IS DRIVEN BY A FIELD THE SERVER ACTUALLY SENDS, and
   * every one of those reads is an explicit presence check that already existed
   * on this drawer for the banner or panel it belongs to. Absent renders NO
   * marker — a tab with nothing to say wears nothing, never a "0" or an
   * all-clear, because an all-clear is a claim and this surface cannot make it.
   */

  /**
   * OVERVIEW — N merged journeys folded into this card, as a number pill.
   *
   * A COUNT, NOT A WARNING: the Overview panel's "Merged into this deal" list
   * is bounded reading, and the money risk inside it has its OWN louder
   * surfaces (the banners above the tabs and the Payments dot below). Read off
   * the card's `mergedDuplicateDealIds` rather than `detail.mergedDuplicates`
   * so the pill is up on the first paint, and `?? []` because a clinic-service
   * that predates duplicates omits the key entirely.
   */
  const mergedCount = (deal.mergedDuplicateDealIds ?? []).length;

  /**
   * PAYMENTS — a possible DOUBLE CHARGE, as the outstanding dot.
   *
   * ONE SERVER FLAG, READ FROM BOTH ENDS OF THE PAIR, because the pair is
   * readable from either end and an admin has the same job whichever half they
   * opened: this card is a merged duplicate that still carries a charge
   * (`paymentReviewRequired` on the card), or paid duplicates were merged INTO
   * it (`paymentReviewRequired` on those rows, via `paidMergedDuplicates`).
   *
   * WAITING AND NOT ALERT, deliberately. It is money, not medicine — putting
   * it on the clinical triangle is exactly how that triangle stops being read.
   */
  const doubleChargeRisk = needsPaymentReview(deal)
    ? 'Possible double charge \u2014 this journey was merged and still carries a charge'
    : paidDuplicates.length > 0
      ? `Possible double charge \u2014 ${paidDuplicates.length} merged ${
          paidDuplicates.length === 1 ? 'journey carries' : 'journeys carry'
        } a charge`
      : null;

  const tabs: TabDef[] = [
    {
      id: 'overview',
      label: 'Overview',
      ...(mergedCount > 0
        ? {
            marker: {
              kind: 'count' as const,
              count: mergedCount,
              name: `${mergedCount} merged ${mergedCount === 1 ? 'journey' : 'journeys'} folded into this deal`,
            },
          }
        : {}),
    },
    ...(showQuestionnaire ? [{ id: 'questionnaire', label: 'Questionnaire' }] : []),
    // The Review tab carries the key-alerts marker: a clinician who has not
    // opened it must still see there is something in there to read. This is the
    // ONE alert marker on the drawer, and `reviewTabWarning` already collapses
    // its two sources one-way (safety flag beats edit marker) for that reason.
    ...(showReviewTab
      ? [
          {
            id: 'review',
            label: 'Review',
            ...(reviewTabWarning ? { marker: { kind: 'alert' as const, name: reviewTabWarning } } : {}),
          },
        ]
      : []),
    // MONEY: the Payments tab exists ONLY for admins — clinicians never see it,
    // and therefore never see its marker either (the marker rides the tab).
    ...(role === 'ADMIN' && deal.orderCaseRef
      ? [
          {
            id: 'payments',
            label: 'Payments',
            ...(doubleChargeRisk ? { marker: { kind: 'waiting' as const, name: doubleChargeRisk } } : {}),
          },
        ]
      : []),
    {
      id: 'activity',
      label: 'Activity',
      // The outstanding ask, as the outstanding dot. `dealActionNeeded` is
      // `required === true` only, so an older clinic-service and an explicit
      // `false` both render nothing. It points HERE because the ask's note is
      // in this feed — which is what DealActionBanner tells the reader to do
      // when the handover carried no note ("check the Activity tab").
      ...(actionNeeded
        ? { marker: { kind: 'waiting' as const, name: 'Action needed \u2014 this deal is waiting on an admin' } }
        : {}),
    },
    { id: 'history', label: 'History' },
    { id: 'audit', label: 'Audit log', floatRight: true },
  ];

  /* ── THE ADMIN MONEY SUMMARY (2026-09-06) ────────────────────────────────
   *
   * ⚠️ MONEY: ADMIN ONLY, AND THE GATE IS THE PAYMENTS TAB'S GATE VERBATIM —
   * `role === 'ADMIN' && deal.orderCaseRef`. Clinicians never see money on the
   * pipeline (the one carve-out is their OWN earnings, which is not this), and
   * `clinicians/clinicians.spec.tsx` sweeps this drawer's whole DOM for
   * `£ / pence / price` on every clinician tab. Copying the tab's own gate
   * rather than inventing a second one means the summary and the tab can never
   * disagree about who is allowed to see a figure.
   *
   * WHY IT SITS ABOVE THE TABS. An admin opening a paid card is deciding
   * whether to chase, refund or leave it, and the first month and the term's
   * value are what that decision turns on. Behind the Payments tab they cost a
   * click and the knowledge that the tab exists; the whole reason the
   * action-needed banner moved above the tabs is the same one.
   *
   * ⚠️ BOTH FIELDS OR NOTHING. `monthlyPence` is ADMIN-projection-only and
   * nullable, `months` is nullable, and an orders revision may send neither. A
   * missing price renders NO block — never "£0.00", which would assert a free
   * plan to the one role that can act on a charge.
   *
   * ⚠️ PLAN VALUE IS ARITHMETIC, AND IT SHOWS ITS WORKING. `monthlyPence ×
   * months` is a portal-side product, not a figure orders sent, so the sum is
   * printed under it ("3 months × £49.99"). An admin can then see it is the list
   * price of the term rather than an amount anybody was charged — which
   * matters because a term with a discounted month would make the product
   * wrong, and the reader is the person who would spot that.
   */
  const money =
    role === 'ADMIN' && deal.orderCaseRef && reviewOrder && reviewOrder !== 'missing' ? reviewOrder : null;
  const monthlyPence = typeof money?.monthlyPence === 'number' ? money.monthlyPence : null;
  const planMonths = typeof money?.months === 'number' && money.months > 0 ? money.months : null;
  /**
   * THE STATE TRAVELS WITH THE FIGURE, ALWAYS (found in review, 2026-09-06).
   *
   * The first cut gated only on role + an order existing, so a REFUNDED,
   * REJECTED or CANCELLED case printed "First month £49.99 / Plan value
   * £149.97" permanently, on every tab, with nothing saying the money had gone
   * back. The Payments tab leads with `Order state` BEFORE the amount for
   * exactly this reason; hoisting the amount above the tabs and leaving the
   * qualifier a click away is how a number stops meaning what it says.
   *
   * Qualified rather than suppressed: an admin looking at a refunded case
   * still wants to know what it was worth — they just must not read it as
   * money we are holding.
   */
  const moneyState = typeof money?.state === 'string' ? money.state.replace(/_/g, ' ') : null;

  // The per-owner threshold that fires escalation/parking, or null on an
  // ordinary deal. Read ONCE here and spent on the banner above the tabs — see
  // that banner for why the warning does not live beside the tally it
  // summarises, and stageClarity's `attemptThreshold` for the derivation it
  // shares with the board card's "Call limit" chip.
  const callLimit = attemptThreshold(deal);

  // §9 — the SERVER-derived active-round attempt count (null hides all the
  // attempt chrome: no round active, or an older server without the fields).
  const roundAttempts = activeRoundAttempts(deal);
  const roundExhausted = roundAttempts !== null && roundAttempts >= REQUIRED_ROUND_ATTEMPTS;

  /**
   * THE HEADER'S ONE AGE LINE (2026-09-06) — replaces three statements of the
   * SAME INTEGER.
   *
   * Until today `daysInStage` was printed three times on one open drawer: a
   * `17D IN STAGE` chip up here, an "In this stage → 17d" row in the Overview
   * Progress column, and a sentence "In this stage 17 days." under it. Three
   * renderings of one server number, each with its own wording and its own
   * rotting rule, is three places to disagree and nothing extra said. It is
   * ONE quiet line now, under the name, and it picks up `createdAt` on the way
   * — the two dates a person wants on opening a card ("how long has this sat?"
   * and "how old is it?") in the one place, on the FROZEN header, so they stay
   * readable from whichever tab is open.
   *
   * ⚠️ THE STAGE CHIP STAYS. It is the deal's STATE, not its age, and it is the
   * one thing here the board card is also keyed on.
   *
   * ROTTING SURVIVES THE COLLAPSE, IN WORDS. The old chip carried the server's
   * `rotting` flag as a danger tone and the old sentence spelled it out; losing
   * both would drop a real server fact. It rides this line as the visible words
   * "needs attention" plus the triangle — never the colour alone (WCAG 1.4.1).
   */
  const stageAge = `${deal.daysInStage} ${deal.daysInStage === 1 ? 'day' : 'days'} in stage`;

  const title = (
    <span style={{ display: 'block' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {/* Struck through on a test record (2026-08-26 §5) — the same void-data
            cue the board card wears; the muted Test chip below still names it. */}
        {/* (a) THE `[Duplicate]` MARKER — BEFORE the name, the Asana model
            literally. It travels with the name wherever the header is read or
            screenshotted, which a chip further along the row would not; and it is
            deliberately NOT the test record's strike-through, because a merged
            card is real data about a real patient who may have really paid. */}
        {mergedInto ? <DuplicateMarker /> : null}
        {/* `[Superseded]` — the SYSTEM pointer (S4.1), with the name exactly
            as on the board card; never the staff `[Duplicate]` word. */}
        {superseded ? <SupersededMarker /> : null}
        <span style={deal.isTest ? { textDecoration: 'line-through' } : undefined}>{dealName(deal)}</span>
        <Badge tone={toneFor(deal.stage)}>{STAGE_LABEL[deal.stage]}</Badge>
        {/* PROVENANCE, DIRECTLY BESIDE THE STATE (2026-09-06) — the two facts a
            reader pairs on sight: what this deal IS, and where it came from. The
            days-in-stage badge used to sit between them and has moved to the
            quiet line below (see `stageAge`), which is what closed the gap.

            §4 (2026-08-26): the SAME compact icon chip the board card wears, so
            a card recognised on the board is recognised here — imported, never
            redrawn, because the glyph and tone are statusChips.tsx's to change
            (full wording via title + aria-label; SQL stays a text badge). The
            Overview "Entered via" row keeps the words. */}
        <EntryPathChip entryPath={deal.entryPath} />
        {/* Test-record flag — muted on purpose: it must be readable, never louder
            than the clinical/stage chips (the same chip the board card wears). */}
        {deal.isTest ? <Badge tone="muted">Test</Badge> : null}
        {/* WHAT IS STILL OWED A PRESCRIBER (2026-09-12) — the existing Badge
            vocabulary, no new pill grammar, and deliberately NOT in the board
            card's single clinical-status slot: this is not a DECISION, it is
            outstanding work, and the CTO has already had one orthogonal marker
            evict a real status from that slot (see `ReviewParkedChip`). It
            renders nothing until the drawer has resolved the order. */}
        <PrescribingFlagChip flags={prescribingFlags} />
        {/* §9 — the active round's attempt chip (danger at 3: handover due). */}
        {roundAttempts !== null && roundAttempts >= 1 ? (
          <Badge tone={roundExhausted ? 'danger' : 'neutral'}>
            Attempt {roundAttempts}/{REQUIRED_ROUND_ATTEMPTS}
          </Badge>
        ) : null}
        {/* Review parked (2026-08-27, CTO) — LAST, exactly as on the board card.
            REQUIRED here, not decoration: the park used to be a member of the
            ClinicalStatus union, so it reached this drawer through the Overview
            "Clinical status" row. Now that it is its own orthogonal marker, that
            row reports the outstanding REVIEW instead, and without this chip the
            drawer would simply stop mentioning the park — a fact silently lost
            on the surface staff open to understand a stalled deal. */}
        {isReviewParked(deal) ? <ReviewParkedChip /> : null}
      </span>
      <span
        style={{
          display: 'block',
          marginTop: '4px',
          fontSize: '12.5px',
          fontWeight: 400,
          // Rotting recolours the line, but the WORDS below are what carry it.
          color: deal.rotting ? 'var(--gps-warn-text)' : 'var(--gps-muted)',
        }}
      >
        {deal.rotting ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '4px' }}>
            <WarningIcon size={13} />
          </span>
        ) : null}
        {stageAge}
        {deal.rotting ? ' \u2014 needs attention' : ''}
        {' \u00b7 created '}
        {formatUtcDate(deal.createdAt)}
        {/* "updated …" — the activity order's sort key (S4.5), so the drawer
            says the same thing the card footer does. Absent → nothing. */}
        {deal.lastActivityAt ? ` \u00b7 updated ${formatUtcDate(deal.lastActivityAt)}` : ''}
      </span>
      {/* "Submitted as …" (S4.5) — beside the card's name, the CTO's
          maiden/married-name rule. The qualifier is VISIBLE text. */}
      <SubmittedAsLine card={deal} style={{ display: 'block', marginTop: '4px', fontWeight: 400 }} />
    </span>
  );

  // The footer CTAs (UX Wave 4 §4 — NOT two competing controls): the FIRST
  // MANUAL_TARGETS entry is the most likely next stage (the server map's own
  // ordering) = the primary "Move to <stage>" button; the rest sit behind the
  // secondary ⋯ menu; "Activity" jumps to that tab as the next primary action.
  // §9: once the active round hits 3 attempts, the HANDOVER becomes the
  // prompted primary action — promoted to the front and relabelled with the
  // channel's own verb ("Hand over to admin" / "Hand back to clinician") so
  // the due next step is explicit, not just "Move to <stage>".
  // ⚠️ `movesFor(deal)`, NOT `MANUAL_TARGETS[deal.stage]` (2026-09-05). The
  // merged `clinical_review` lane holds two populations with opposite rules,
  // and the table can only describe the stage: a PAID card there is refused
  // every manual exit by the server, exactly as the old
  // `awaiting_clinical_review` was. Indexing the map by stage would put a
  // primary "Close as lost" button on an approved, paid sale and let the
  // operator find out from a 409.
  // ⚠️ AND `role` IS NOT OPTIONAL (2026-09-22): the admin-only move to
  // `unreachable` must not appear in a clinician's ⋯ menu, where its only
  // possible outcome is a 403.
  const moves = movesFor(deal, role);
  const handoverMove = moves.find((m) => m.via === 'handover');
  const primaryMove = roundExhausted && handoverMove ? handoverMove : moves[0];
  const otherMoves = moves.filter((m) => m !== primaryMove);
  const primaryLabel =
    primaryMove && roundExhausted && primaryMove.via === 'handover'
      ? moveLabel(primaryMove)
      : primaryMove
        ? `Move to ${STAGE_LABEL[primaryMove.to]}`
        : null;

  // THE SPLIT (2026-08-27, CTO drawer review) — see LOGGED_KINDS for the rule
  // and why it is an INCLUDE list. A PARTITION of one sorted feed: every entry
  // lands in exactly one of the two lists, so relocating a row out of Activity
  // always MOVES it into the Audit log and can never drop it from the UI.
  //
  // 2026-08-26 §1: DISPLAYED newest-first (the server aggregate stays
  // oldest-first on the wire) — staff open the drawer to see what happened
  // LAST, not to re-read the origin story. Sorted ONCE, display-side, so the
  // composer's append-to-feed refetch and the audit tab both inherit it.
  // `.slice()` first: `.sort` mutates, and the source array is React state.
  //
  // 2026-08-27 (CTO audit-log review) — CONFIRMED, NOT CHANGED. "Oldest item
  // towards bottom" is exactly what newest-first descending produces, so this
  // comparator already satisfied the ask and was deliberately left alone. Kept
  // as a note because the ask arrived alongside a re-layout of the row itself
  // (see ActivityRow): a future reader must not conclude the ordering was
  // missed. journey-ux.spec.tsx §1 pins the direction on BOTH tabs.
  const newestFirst = (a: DealActivityEntry, b: DealActivityEntry) => b.at.localeCompare(a.at);
  const sortedActivity = (detail?.activity ?? []).slice().sort(newestFirst);
  const feedEntries = sortedActivity.filter((entry) => LOGGED_KINDS.has(entry.kind));
  const auditEntries = sortedActivity.filter((entry) => !LOGGED_KINDS.has(entry.kind));

  // §1 — resolve feed actor ids to display names for an ADMIN viewer, in ONE
  // batched lookup per newly-seen set (the exact admin/AuditLogTab.tsx
  // pattern). GATED ON ROLE: the lookup endpoint is ADMIN-only, so a
  // CLINICIAN drawer must never even issue the request — their actor display
  // is the role chip alone. A failed lookup leaves the chips/raw feed intact
  // (never an error state) and clears the ids so a later batch can retry. Ids
  // that resolve to nothing (e.g. a PATIENT actor — the staff lookup does not
  // know them) simply stay name-less.
  //
  // 2026-08-27 (stage clarity): the CLOSURE author and the ACTION-NEEDED asker
  // join the SAME batch rather than issuing lookups of their own. Both are
  // usually already in the feed's id set, so folding them in normally costs
  // nothing — and when the closure predates the feed window it is one extra id
  // on the one request, never a second round trip.
  // 2026-09-06: the people who MERGED a duplicate join the same batch. Same
  // argument as the two above — they are usually already in the feed's id set,
  // and when they are not it is one extra id on the one request rather than a
  // second round trip. Without it the canonical's merge list could name a role
  // and never a person, on rows that record a staff action which REMOVES A CARD
  // FROM THE BOARD.
  const attributionActors = [
    outcome?.closedByUserId,
    actionNeeded?.askedByUserId,
    ...(detail?.mergedDuplicates ?? []).map((row) => row.mergedByUserId),
  ];
  const unresolvedActors =
    role === 'ADMIN'
      ? [
          ...new Set(
            [...(detail?.activity ?? []).map((e) => e.actorUserId), ...attributionActors].filter(
              (id): id is string => id != null,
            ),
          ),
        ].filter((id) => !lookedUp.current.has(id))
      : [];
  // NOTE the set deliberately still carries SERVICE PRINCIPALS (`svc:…` — the
  // order-sync's actor on every won deal). They are stripped inside
  // `lookupStaffUsers`, at the API boundary, so ONE guard protects every caller
  // of that endpoint rather than each screen remembering to filter. Re-adding
  // the same filter here would only make that guard unreachable from this
  // screen's tests, which is how a fix quietly becomes a no-op.
  //
  // The id set is the effect's real input, so key on it rather than re-running
  // after every render (which is what the missing dependency array used to do).
  const unresolvedActorsKey = unresolvedActors.join(',');
  useEffect(() => {
    if (unresolvedActors.length === 0) return;
    for (const id of unresolvedActors) lookedUp.current.add(id);
    lookupStaffUsers(unresolvedActors)
      .then(({ users }) => {
        setActorDirectory((prev) => {
          const next = { ...prev };
          // The NAME is `fullName` when there is one, else the email — a
          // name-less legacy account (minted before createInvite required a
          // full name, 2026-07-16) correctly falls back to its email rather
          // than rendering blank. `email` is kept alongside regardless, because
          // the reveal shows it even when it is also standing in as the name.
          for (const { userId, email, fullName } of users) {
            const realName = fullName?.trim();
            next[userId] = { name: realName || email, email, hasRealName: Boolean(realName) };
          }
          return next;
        });
      })
      .catch(() => {
        // DELIBERATELY NOT un-marking the ids (2026-08-28). The ids stay in
        // `lookedUp` so this exact set is never re-requested: clearing them
        // would put us straight back into a loop, because `lookedUp` is a ref
        // — mutating it triggers no re-render, so the retry could only ride on
        // some other render, and by then the dependency key is unchanged and
        // the effect would not fire anyway. The old code had no dependency
        // array at all, so it re-ran after EVERY render and re-fired a
        // guaranteed-400 batch at an ADMIN-only identity route indefinitely.
        // A name-less row is the same rendering as an unresolvable id and is
        // strictly better than hammering identity; closing and re-opening the
        // drawer remounts the component and so retries from a fresh ref.
      });
    // `unresolvedActors` is a new array identity every render;
    // `unresolvedActorsKey` is its stable value-equal digest and is the only
    // input that should re-trigger a batch. The directive must be the LAST
    // comment line before the dependency array or it is silently defeated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolvedActorsKey]);
  // PHOTO CHASES (CTO 2026-08-27) — how many REAL photo-request sends this
  // journey has had, and whether a human now needs to step in. Absent means
  // "not reported" (a clinic-service predating the counter), which reads as 0
  // rows rendered rather than a claim that nobody was chased.
  const photoChases = detail?.assessment?.photoChaseCount ?? 0;
  // `photos` ABSENT is UNKNOWN, not empty: only an explicitly empty gallery
  // proves the patient has still not uploaded, and only that may raise the
  // manual-follow-up prompt. Two chases is the CTO's threshold.
  const photoGallery = detail?.assessment?.photos;
  const photoFollowUpDue = role === 'ADMIN' && photoChases >= 2 && photoGallery !== undefined && photoGallery.length === 0;

  // CLIN-03 — sticky not_eligible (hard stop or submit). Same banner the
  // Questionnaire tab shows, mirrored on Activity + History so every working
  // tab names the verdict for the caller.
  //
  // ⚠️ THIS IS AN ELIGIBILITY FACT AND NOTHING MORE — it says the assessment
  // said no, NOT that the deal is closed. It deliberately does not consult
  // `deal.stage`, and the banner it gates must therefore never assert a stage
  // on the strength of it: `NotEligibleBanner` reads `deal.stage` itself and
  // states the real lane. Until 2026-09-06 all three copies of that Alert
  // hard-coded "the deal has moved to Lost" behind this flag, which is how a
  // card sitting in "Admin follow-up" came to carry a drawer insisting it was
  // Lost. A not-eligible deal in an open admin lane is a designed state (see
  // the block on NotEligibleBanner for the two server routes into it), so the
  // two facts have to stay separate here.
  const notEligible =
    detail?.assessment?.eligibility === 'not_eligible' || deal.eligibility === 'not_eligible';

  // Hard-stop safety net UNDO: a closed_lost deal can be recovered into an
  // active stage with a required reason. Terminal closed_lost has no manual
  // MANUAL_TARGETS move, so this is its own dedicated footer action.
  const isLost = deal.stage === 'closed_lost';

  // CLINICIAN → ADMIN HANDOFF (2026-08-07, CTO decision 4). A clinician must
  // NEVER stop a patient's plan — that is a money action and it lives with an
  // admin — so the ONLY control this feature adds is "hand it over with a note".
  // Offered on any live deal (the server takes it from CLINICIAN and ADMIN, and
  // 409s a terminal one), which is exactly where the double-prescribing advisory
  // is read: an AUTHORISED card in `clinical_review` has no manual move at all
  // (the old `awaiting_clinical_review` padlock, re-keyed onto a row column by
  // the 2026-09-05 merge and onto `holdAuthorisedAt` by the 2026-09-07
  // authorisation gate), so without this the clinician's footer would offer them
  // nothing to do about what they found on the very cards they find it on.
  const canSendForAdminReview = !TERMINAL_STAGES.includes(deal.stage);

  async function runRecover(reason?: string) {
    if (!reason) return; // ConfirmDialog gates confirm behind a reason; belt-and-braces
    setRecoverBusy(true);
    setRecoverError(null);
    try {
      await recoverDeal(deal.dealId, reason);
      setRecoverOpen(false);
      onChanged(); // the board reloads; the recovered deal leaves the Lost column
      onClose(); // the drawer re-opens fresh from the reloaded board if reselected
    } catch (err) {
      // A 409 (only a lost deal can be recovered) carries a PHI-free message.
      setRecoverError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not recover this deal. Please try again.',
      );
      setRecoverBusy(false);
    }
  }

  /**
   * Reopen a SUPERSEDED card (ADMIN; clinic S4.1 `reopen-superseded`). The
   * drawer stays open and refreshes, like the admin-review handoff: the stage
   * badge flips back to the lane the card was closed from and the Superseded
   * banner goes, so the reopen visibly happened. A refusal (409 "not
   * superseded", 403) stays inside the open dialog in the server's words.
   */
  async function runReopenSuperseded(reason?: string) {
    if (!reason) return; // ConfirmDialog gates confirm behind a reason; belt-and-braces
    setReopenBusy(true);
    setReopenError(null);
    try {
      await reopenSupersededDeal(deal.dealId, reason);
      setReopenOpen(false);
      setReopenBusy(false);
      setReopenNotice('Card reopened. It is back in the lane it was closed from.');
      load();
      onChanged();
    } catch (err) {
      setReopenError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not reopen this card. Please try again.',
      );
      setReopenBusy(false);
    }
  }

  /**
   * Hand the deal to an admin with the REQUIRED note.
   *
   * Unlike Recover, the drawer stays OPEN and simply refreshes: the deal is
   * still this clinician's to read, the header badge flips to Pending admin
   * review and the note appears on the Activity feed, so the handoff visibly
   * happened rather than the screen vanishing under them.
   */
  async function runSendForAdminReview(note?: string) {
    if (!note) return; // ConfirmDialog gates confirm behind a note; belt-and-braces
    setHandoffBusy(true);
    setHandoffError(null);
    try {
      await dealPendingAdminReview(deal.dealId, note);
      setHandoffOpen(false);
      setHandoffBusy(false);
      load(); // the header stage + the note on the Activity feed
      onChanged(); // the board reloads; the card moves into Pending admin review
    } catch (err) {
      // A 409 (a terminal deal cannot be handed over) carries a PHI-free message.
      setHandoffError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not send this deal for admin review. Please try again.',
      );
      setHandoffBusy(false);
    }
  }

  /**
   * Mark the deal — and, when one is linked, its order case — as TEST data.
   *
   * TWO services own the truth (orders-service case, clinic-service deal), so
   * this is two calls, not one — and the ORDER goes FIRST. The deal half
   * CLOSES the deal (closed_lost, reason "test record") and the default board
   * list then hides the card, so deal-first would let a partial failure
   * close+hide the deal while its order stayed "real" on the admin money
   * boards — with the vanished card taking the retry affordance with it.
   * Order-first can strand nothing: an order-half failure marks nothing and
   * hides nothing (retry re-runs both halves from the top), and a deal-half
   * failure after order success keeps the deal visible while the retry
   * re-fires ONLY the deal half (see markTestOrderDone) — re-posting the
   * landed order half would double an audit row for one human action.
   */
  /**
   * Open the mark-test dialog and work out, BEFORE the operator can press
   * anything, which order case (if any) this mark has to flag.
   *
   * The ref short-circuits it — no request when clinic-service already told us.
   * Otherwise the assessment-keyed lane, and a FAILED read resolves to `'none'`
   * rather than blocking: `'none'` renders as "no linked order was found" plus
   * the Payments-board route, which is true of an outage and of a genuinely
   * order-less deal alike, and never strands the mark behind a read that keeps
   * failing. See the `markOrderLink` note for the whole argument.
   */
  function openMarkTest() {
    setMarkTestError(null);
    setMarkTestOrderDone(false);
    setMarkTestOpen(true);
    if (deal.orderCaseRef) {
      setMarkOrderLink({ state: 'found', orderCaseId: deal.orderCaseRef });
      return;
    }
    if (!deal.assessmentId) {
      // No ref and no assessment: there is no key to look one up BY. Every
      // order case on this estate is built from an assessment, so this is the
      // pre-checkout lead — but the dialog still says only what it knows.
      setMarkOrderLink({ state: 'none' });
      return;
    }
    setMarkOrderLink({ state: 'checking' });
    const assessmentId = deal.assessmentId;
    getStaffQueue()
      .then((orders) => {
        const match = orders.find((o) => o.assessmentRef === assessmentId);
        setMarkOrderLink(match ? { state: 'found', orderCaseId: match.orderCaseId } : { state: 'none' });
      })
      .catch(() => setMarkOrderLink({ state: 'none' }));
  }

  /**
   * Open the cascade report — but only when there is something to report.
   *
   * A clean cascade closes the dialog exactly as it always did: an operator who
   * is shown a modal after every single mark stops reading them, and the one
   * that matters is then the one they click through. Silence here is the same
   * claim it has always been ("the deal is marked") and never a new claim about
   * the other two records — see `cascadeIsEventful` for why an ABSENT cascade
   * is also silent rather than green.
   */
  function showCascade(direction: CascadeDirection, cascade: DealTestCascade | undefined, reason?: string) {
    setCascadeError(null);
    if (!cascadeIsEventful(cascade) || !cascade) return;
    setCascadeReport({ direction, cascade, reason });
  }

  /**
   * FINISH AN INCOMPLETE CASCADE by re-posting the identical request. That is
   * the server's own documented recovery path — every leg is an idempotent
   * desired-state write, so the parts that landed are not repeated and only the
   * outstanding ones run.
   *
   * ⚠️ THE DEAL HALF ONLY, deliberately: the order half of `runMarkTest` (the
   * direct orders-service call) has already landed by the time any cascade
   * exists, and re-firing it would write a second audit row for one human
   * action. The re-run REPLACES the report with the new answer, so an operator
   * who fixes the underlying problem watches the legs turn over rather than
   * having to re-open the deal to find out whether it worked.
   */
  async function rerunCascade() {
    if (!cascadeReport) return;
    setCascadeBusy(true);
    setCascadeError(null);
    try {
      const result = await markDealTest(
        deal.dealId,
        cascadeReport.reason,
        cascadeReport.direction === 'unmark' ? { revert: true } : {},
      );
      setCascadeBusy(false);
      if (!result.cascade) {
        // The retry reached a server that cannot report. Say that, rather than
        // leaving the previous answer on screen as if it were the new one.
        setCascadeError('This finished, but the server did not say what happened to the plan and the patient.');
        return;
      }
      setCascadeReport({ ...cascadeReport, cascade: result.cascade });
      load();
      onChanged();
    } catch (err) {
      // A PHI-free refusal surfaces verbatim (the parseError lane) — the 409
      // for a deal other duplicates point at is the actionable one.
      setCascadeError(
        err instanceof ApiError && err.detail ? err.detail : 'Sorry — the re-run did not work. Please try again.',
      );
      setCascadeBusy(false);
    }
  }

  async function runMarkTest(reason?: string) {
    setMarkTestBusy(true);
    setMarkTestError(null);
    const orderCaseId = markOrderLink?.state === 'found' ? markOrderLink.orderCaseId : null;
    /**
     * ⚠️ A LOCAL COPY, BECAUSE THE STATE ONE IS A RENDER-OLD CLOSURE. The catch
     * below has to know whether the order half landed DURING THIS press, and
     * `markTestOrderDone` still reads `false` there — `setMarkTestOrderDone`
     * schedules a re-render, it does not rewrite the variable this function
     * closed over. Reading the state value there produced the wrong sentence
     * ("we could not mark this record") on the one lane where the order IS
     * already marked, which is the partial failure staff most need described
     * accurately. The state setter still fires: it is what relabels the primary
     * to "Retry deal marking" and makes the retry skip the landed half.
     */
    let orderMarked = markTestOrderDone;
    if (orderCaseId && !orderMarked) {
      try {
        await markOrderTest(orderCaseId, reason);
        orderMarked = true;
        setMarkTestOrderDone(true);
      } catch (err) {
        // The ORDER half failed FIRST — nothing is marked, closed or hidden
        // yet; say so, and the retry re-runs both halves from the top. A
        // PHI-free refusal surfaces VERBATIM (the parseError lane): the
        // orders sibling now 409s while the case still has an ACTIVE
        // subscription, and staff must be told to cancel it first — a bare
        // "retry" would just loop them into the same refusal.
        setMarkTestError(
          err instanceof ApiError && err.detail
            ? err.detail
            : 'Order marking failed — nothing is marked yet. Retry.',
        );
        setMarkTestBusy(false);
        return;
      }
    }
    try {
      const result = await markDealTest(deal.dealId, reason);
      setMarkTestOpen(false);
      setMarkTestBusy(false);
      setMarkTestOrderDone(false);
      setMarkOrderLink(null);
      /* THE OTHER TWO RECORDS, REPORTED (2026-09-06). The deal is committed
         either way — that is why this runs on the success path and never
         blocks the reload below. `cascadeIsEventful` is silent for the ordinary
         clean cascade and for an older clinic-service that cannot answer at
         all; everything else opens the report, including the WITHHELD leg,
         which is the safety rule working rather than an error. */
      showCascade('mark', result.cascade, reason);
      load(); // the drawer header grows the TEST chip on the now-closed deal
      onChanged(); // the board reloads — the DEFAULT list now hides the card
    } catch (err) {
      if (orderMarked) {
        // Partial failure AFTER order success — the deal is still open and
        // visible (nothing closed or hidden), and the retry re-fires ONLY
        // this half.
        //
        // ⚠️ KEYED ON WHETHER THE ORDER HALF LANDED, NOT ON WHETHER ONE WAS LINKED.
        // The old condition was `deal.orderCaseRef`, which asked "could there
        // have been an order half?" instead of "did one actually land?" — so a
        // deal WITH a ref whose order half was skipped (because it had already
        // landed on a previous press) reported the wrong sentence, and a
        // resolved-but-unmarked order reported it too. The flag is set exactly
        // when the order POST returned 2xx, which is the fact this message
        // claims.
        setMarkTestError('Order marked; deal marking failed — retry.');
      } else {
        // Nothing is marked: either there was no order half to run, or it was
        // skipped because none could be found. A PHI-free refusal (e.g. already
        // marked on the server) carries its own message.
        setMarkTestError(
          err instanceof ApiError && err.detail
            ? err.detail
            : 'Sorry — we could not mark this record as a test. Please try again.',
        );
      }
      setMarkTestBusy(false);
    }
  }

  /**
   * UNMARK a test record (2026-08-26 §5) — the revert of the DEAL half only
   * (`revert: true` on the same endpoint, clinic#137). Deliberately NOT the
   * two-service dance runMarkTest runs: un-flagging an order on the money
   * ledgers is an admin decision made where the money lives. FLAG-ONLY on the
   * server too: the revert never changes the stage in either direction — a
   * deal the mark closed STAYS closed_lost, and reopening it is the separate
   * "Recover lead" action (which also clears the flag), so the two undo lanes
   * never overlap and no stage transition is ever fabricated.
   */
  async function runUnmarkTest(reason?: string) {
    setUnmarkBusy(true);
    setUnmarkError(null);
    try {
      const result = await markDealTest(deal.dealId, reason, { revert: true });
      setUnmarkOpen(false);
      setUnmarkBusy(false);
      /* THE REVERT CASCADES TOO, and its withheld sentence is the mirror image
         ("their account was left hidden … un-mark those deals too"), so the
         undo lane owes the operator the same report the mark lane does. It was
         the same discarded response object. */
      showCascade('unmark', result.cascade, reason);
      load(); // the header sheds the Test chip + the strikethrough
      onChanged(); // the board reloads — the deal counts as real again, in whatever stage it kept
    } catch (err) {
      // A PHI-free refusal (e.g. not currently marked) surfaces verbatim.
      setUnmarkError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not unmark this record. Please try again.',
      );
      setUnmarkBusy(false);
    }
  }

  /**
   * MARK this deal as a duplicate of `canonicalDealId`, or RELEASE it.
   *
   * ONE handler for both directions, because the server is one endpoint and the
   * two share every failure path. The direction is the argument, not a second
   * function that could drift.
   *
   * ⚠️ THE PAYMENT BLOCK IS KEPT AND SHOWN AFTER THE DIALOG CLOSES, and that is
   * the point of the whole response. The operator has just hidden a card that
   * may carry a REAL, un-refunded charge — marking a duplicate deliberately
   * cascades to nothing in orders-service — so "the card disappeared" cannot be
   * the last thing this surface says. It is surfaced in BOTH directions: a
   * release means the review is no longer needed, and the operator should be
   * told that too rather than left wondering.
   */
  async function runMarkDuplicate(input: { canonicalDealId: string } | { revert: true }, reason?: string) {
    const reverting = 'revert' in input;
    if (dupBusy) return;
    setDupBusy(true);
    setDupError(null);
    try {
      const res = await markDealDuplicate(
        deal.dealId,
        reverting ? { revert: true, reason } : { canonicalDealId: input.canonicalDealId, reason },
      );
      setDupMode(null);
      setDupPayment(res.payment);
      load(); // the drawer grows (or sheds) the [Duplicate] marker and the banner
      onChanged(); // the board reloads — the card leaves, or returns to its banked lane
    } catch (err) {
      // ⚠️ THE DIALOG STAYS OPEN ON FAILURE, unlike the evidence lanes. Every
      // refusal here is CORRECTABLE IN PLACE — pick a different canonical, or
      // go and un-mark the dependents the message names — and closing the
      // dialog would throw away the search the operator just typed and the
      // reason they wrote. The one thing it must not do is fail silently.
      setDupError(duplicateRefusal(err instanceof ApiError ? err.detail : undefined, reverting));
    } finally {
      setDupBusy(false);
    }
  }

  return (
    <>
    <Drawer
      open
      onClose={onClose}
      title={title}
      width="twoThirds"
      footer={
        status === 'ready' ? (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* Hard-stop safety net UNDO — a Lost deal is recoverable (both
                roles work the pipeline; the server captures the required reason). */}
            {isLost ? (
              <Button
                type="button"
                variant="primary"
                style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
                onClick={() => {
                  setRecoverError(null);
                  setRecoverOpen(true);
                }}
              >
                Recover lead
              </Button>
            ) : null}
            {/* The clinician→admin handoff. A SECONDARY control on purpose: it
                is the escape hatch for "only an admin can action this", not the
                normal next step for a deal. It stops nothing. */}
            {canSendForAdminReview ? (
              <Button
                type="button"
                variant="ghost"
                style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
                onClick={() => {
                  setHandoffError(null);
                  setHandoffOpen(true);
                }}
              >
                Send for admin review
              </Button>
            ) : null}
            {primaryMove ? (
              <Button
                type="button"
                variant="primary"
                style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
                onClick={() => onRequestMove(deal, primaryMove)}
              >
                {primaryLabel}
              </Button>
            ) : null}
            {/* The ⋯ kebab now ALWAYS renders (2026-08-26 §5): the remaining
                legal moves as before, PLUS the test-record housekeeping —
                "Mark as test record" moved here from the footer button row
                (it is bookkeeping, not deal work, and the footer was growing
                crowded), sitting ABOVE Close as lost in neutral tone; a deal
                already flagged offers "Unmark test record" instead (the
                server revert, clinic#137). On a stage with no remaining
                moves the menu simply holds the housekeeping rows. */}
            <Menu
              menuLabel="More moves"
              triggerLabel="More moves"
              // The footer sits at the bottom of the viewport — open upward.
              menuStyle={{ top: 'auto', bottom: 'calc(100% + 8px)' }}
            >
              {({ close }) => (
                <>
                  {/* ⚠️ `unreachable` IS EXCLUDED HERE AND RENDERED BESIDE
                      "Close as lost" BELOW (2026-09-23, CTO). Both rows are the
                      same admission — we are giving up on reaching this patient
                      — and they were split across the top and bottom of the
                      menu with the housekeeping marks between them. Grouping
                      them puts the two endings together and leaves this block
                      to the moves that keep the deal in play. */}
                  {otherMoves
                    .filter((move) => move.to !== 'closed_lost' && move.to !== 'unreachable')
                    .map((move) => (
                      <MenuItem
                        key={move.to}
                        onSelect={() => {
                          close();
                          onRequestMove(deal, move);
                        }}
                      >
                        {moveLabel(move)}
                      </MenuItem>
                    ))}
                  {/* ══ THE WARM-LEAD CALLING LANE (2026-09-08, CTO) ══════
                      *"Occasionally we should provide them the ability to call
                      the warm leads … It won't be a primary workflow."*

                      ⚠️ IN THE ⋯ MENU, AND THAT IS THE FEATURE. There is no
                      board column, no toolbar chip and no queue — a desk comes
                      here deliberately, on one card, having decided to ring
                      that person. Anything more prominent would put a sales
                      lane next to the callbacks queue, which is the one on this
                      board currently earning.

                      ⚠️ NOT the same row as "Move to Callback" above it, which
                      mints a REAL ticket with a 3-then-3 escalation machine for
                      a patient who ASKED us to ring. This logs a call and
                      touches no ticket at all. Both are legitimate and they are
                      deliberately two rows.

                      Offered only where the server accepts it (`isLeadCallCard`
                      reads the card's own server-computed `roundOwner`): a deal
                      somebody is already assigned to ring is logged on its
                      round, and clinic-service 409s this route there. */}
                  {isLeadCallCard(deal) ? (
                    <MenuItem
                      onSelect={() => {
                        close();
                        setLeadCallOpen(true);
                      }}
                    >
                      Call this lead…
                    </MenuItem>
                  ) : null}
                  {!deal.isTest ? (
                    <MenuItem
                      onSelect={() => {
                        close();
                        openMarkTest();
                      }}
                    >
                      Mark as test record
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onSelect={() => {
                        close();
                        setUnmarkError(null);
                        setUnmarkOpen(true);
                      }}
                    >
                      Unmark test record
                    </MenuItem>
                  )}
                  {/* ⚠️ BESIDE "Mark as test record", NEVER MERGED WITH IT
                      (2026-09-06, CTO). The two sit together because both are
                      bookkeeping rather than deal work, and they read
                      similarly — which is exactly why the distinction has to
                      be held by the WORDING and by two separate rows. A test
                      record means "this never happened" and marks the deal,
                      the ORDER and the patient ACCOUNT; a duplicate means
                      "this happened twice, to a real person" and marks the
                      deal alone, leaving a possibly-double CHARGE on the
                      payments board where somebody can refund it. Collapsing
                      them into one "hide this card" action would take real
                      money off a real board.

                      Neutral tone, above Close as lost, like its neighbour:
                      merging is reversible (the release below) and the danger
                      slot on this menu belongs to the one action that is not. */}
                  {/* A SUPERSEDED card gets neither duplicate row (S4.5): it is
                      already closed and linked by the SYSTEM pointer, and clinic
                      refuses to stack a staff mark on it ("Reopen it first if it
                      is a separate journey") — the stub mirrors that 409. Its
                      undo is the ADMIN reopen on the banner above the tabs. */}
                  {superseded && !mergedInto ? null : !mergedInto ? (
                    <MenuItem
                      onSelect={() => {
                        close();
                        setDupError(null);
                        setDupPayment(null);
                        setDupMode('mark');
                      }}
                    >
                      Mark as duplicate of…
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onSelect={() => {
                        close();
                        setDupError(null);
                        setDupMode('release');
                      }}
                    >
                      Release from duplicate
                    </MenuItem>
                  )}
                  {/* Directly above "Close as lost" — see the note on the top
                      block. NOT `tone="danger"`: the red is reserved for the
                      consequence, and this lane is one click from coming back,
                      where a close is the end of the deal. */}
                  {otherMoves
                    .filter((move) => move.to === 'unreachable')
                    .map((move) => (
                      <MenuItem
                        key={move.to}
                        onSelect={() => {
                          close();
                          onRequestMove(deal, move);
                        }}
                      >
                        {moveLabel(move)}
                      </MenuItem>
                    ))}
                  {otherMoves
                    .filter((move) => move.to === 'closed_lost')
                    .map((move) => (
                      <MenuItem
                        key={move.to}
                        tone="danger"
                        onSelect={() => {
                          close();
                          onRequestMove(deal, move);
                        }}
                      >
                        {moveLabel(move)}
                      </MenuItem>
                    ))}
                </>
              )}
            </Menu>
            {/* 2026-08-27 restructure (sanctioned): the "Activity" nav button
                is GONE — the tabs are the navigation — and its slot opens the
                Log-activity composer modal instead. ALWAYS offered, like the
                composer it hosts: a fresh lead and a terminal/Lost deal can
                still carry calls, notes and emails. */}
            <Button
              type="button"
              variant="ghost"
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
              onClick={() => setLogOpen(true)}
            >
              Log activity
            </Button>
          </div>
        ) : undefined
      }
    >
      {status === 'loading' ? (
        // Wave 6 §1: drawer-shaped loading — section blocks where the deal's
        // Contact / Progress / activity content is about to land.
        <DrawerSkeleton blocks={3} label="Loading the deal…" />
      ) : status === 'gone' ? (
        <ErrorState
          message="This person is not eligible — the deal is no longer available. Advise the caller, then close and start a new lead if needed."
          onRetry={onClose}
          retryLabel="Close deal"
        />
      ) : status === 'error' || !detail ? (
        <ErrorState message="Sorry — we could not load this deal." onRetry={load} />
      ) : (
        <>
          {/* ⚠️ A FAILED "open that deal" LINK, ABOVE EVERYTHING — including the
              ask and the money banners, which is the one place this outranks
              them. It is not more important than a double charge; it is the
              only thing on this list that is a RESPONSE TO A CLICK the reader
              just made, and an unanswered click is read as a broken link. The
              banners below are standing facts and will still be there.

              `role="alert"` (not the `role="status"` the board's notice used):
              this is an error and it interrupts. Scrolled into view by the
              effect above, because the links that raise it sit far down the
              Overview panel and in the Activity feed. */}
          {linkError ? (
            <div ref={linkErrorRef}>
              <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
                {linkError}
              </Alert>
            </div>
          ) : null}

          {/* THE ASK, ABOVE THE TABS (2026-08-27, stage clarity §2): whatever
              tab this drawer opens on, an outstanding request is the first
              thing in the content area — the point is that nobody has to read
              note history to find out what they were asked to do. Rendered on
              the server's `required` flag alone (R41); absent → nothing. */}
          {actionNeeded ? (
            <DealActionBanner actionNeeded={actionNeeded} actor={actorDirectory[actionNeeded.askedByUserId ?? '']} />
          ) : null}

          {/* ⚠️ THE CALL-ATTEMPT THRESHOLD, ABOVE THE TABS — AND THIS IS THE
              THIRD TIME IT HAS HAD TO BE PUT BACK. Read the block above
              `AttemptThresholdChip` in stageClarity.tsx before touching it.

              THREE CLINICIAN ATTEMPTS AUTO-ESCALATES the callback to admin, and
              THREE ADMIN ATTEMPTS AUTO-PARKS the deal out of the working queue
              as unreachable. Both fire server-side with no human in the loop,
              so a card can leave somebody's lane while they are looking at it.
              Twice now the counters have been re-laid out and the WARNING has
              gone with them; the counters themselves are the cheap half.

              ABOVE THE TABS, NOT INSIDE THE ACTIVITY TAB. The per-owner tally
              belongs at the top of Activity — it is the summary of the
              `call_attempt` rows underneath it, and that is where
              `AttemptSummary` sits. But a warning that only appears once you
              have chosen the right tab is a warning that gets lost, which is
              precisely the history here. So the numbers stay one click away
              and the CONSEQUENCE stands with the other always-visible banners.
              It also makes the sentence's own closing pointer ("the full tally
              is on the Activity tab") true from where it is read.

              VERBATIM FROM `attemptThreshold`, never re-worded here: that
              function derives from `attemptSplitChips`, the same single read
              behind `AttemptSummary` on the Activity tab below, so the tally
              and this banner cannot end up describing different dials. (It said
              "the board card's 'Call limit' chip" until 2026-09-06; the
              four-row card dropped that chip and nothing mounts
              `AttemptThresholdChip` now, so this Alert is the ONLY live
              rendering of the warning — which is also why it must not quietly
              move inside a tab.) Null on an
              ordinary deal and on any clinic-service that does not send the
              split — no threshold, no banner. */}
          {callLimit ? (
            <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {callLimit.full}
            </Alert>
          ) : null}

          {/* ⚠️ THE MONEY FIRST, ON BOTH SIDES OF THE PAIR, AND ABOVE THE MERGE
              NOTICE ITSELF (2026-09-06). Merging a deal cascades to NOTHING in
              orders-service — deliberately, unlike the test mark — so a charge
              on a merged card is still live and still refundable, and the only
              real risk of this whole feature is that the card going quiet takes
              the charge with it. So the double-charge warning outranks the
              "you are on the wrong card" one: if a reader takes exactly one
              thing from this drawer, it must be that there is money to check.
              Danger + role="alert"; everything else about a merge is `info`. */}
          {mergedInto && needsPaymentReview(deal) ? <DuplicatePaymentBanner /> : null}
          {paidDuplicates.length > 0 ? <AbsorbedPaymentBanner count={paidDuplicates.length} /> : null}

          {/* ⚠️ WHAT THE ACTION JUST DID ABOUT THE MONEY — the server's answer,
              said once, at the moment it is actionable (2026-09-06).
              `DuplicatePaymentBanner` above is a STANDING fact and it renders
              off `deal`, which the reload behind this action has not
              necessarily refreshed yet; this is the RECEIPT for a press that
              has just landed, and it carries the one thing the standing banner
              cannot — the order reference the Payments board is searched by.
              Shown in BOTH directions on purpose: after a release, "the review
              is no longer needed" is as useful as the warning was, and an
              operator who saw the warning deserves to be told it is discharged.
              role="status": the drawer around it has already changed visibly. */}
          {dupPayment ? (
            <Alert
              tone={dupPayment.reviewRequired ? 'danger' : 'info'}
              role="status"
              style={{ marginBottom: 'var(--gps-s-3)' }}
            >
              {dupPayment.reviewRequired ? (
                <>
                  <strong>This duplicate has a payment on it.</strong> Marking it here changed nothing on the
                  Payments board — the charge is still live and may need refunding. Search the Payments board
                  for {dupPayment.orderCaseRef ?? 'this patient'}.
                </>
              ) : /* ⚠️ THE MONEY SENTENCE BRANCHES ON `orderCaseRef`, NOT ON
                     `reviewRequired` (2026-09-06). The server derives
                     `reviewRequired = duplicateOfDealId !== null && orderCaseRef
                     !== null`, and ON A RELEASE THE POINTER IS ALREADY CLEARED —
                     so a genuinely PAID card comes back
                     `{reviewRequired: false, orderCaseRef: '<a real ref>'}`.
                     Reading the flag alone told an ADMIN "there is no payment on
                     this deal" about a patient who had been charged.

                     It was the ONLY sentence about money left on screen at that
                     moment: `DuplicatePaymentBanner` unmounts on release too
                     (`mergedInto && needsPaymentReview`). So a double-charged
                     patient who was merged and then un-merged produced one
                     false all-clear and nothing else, and no refund gets
                     raised. `orderCaseRef` is the honest test in both
                     directions — it is present iff there is an order. */
              dupPayment.orderCaseRef !== null ? (
                <>
                  <strong>Released — and this deal has a payment on it.</strong> Releasing it here changed
                  nothing on the Payments board. If it was charged twice, the charge is still live and may
                  need refunding. Search the Payments board for {dupPayment.orderCaseRef}.
                </>
              ) : (
                'Done. There is no payment on this deal, so nothing on the Payments board changes.'
              )}
            </Alert>
          ) : null}

          {/* (b) "Closed and merged into another deal" — ABOVE THE TABS for the
              same reason the action banner is: whatever tab this opens on, the
              reader has to learn they are on a superseded card without first
              knowing where to look. Stronger here than for the ask, in fact —
              this one's message is that the work belongs on a different record.
              Note it is NOT the closure outcome block: `dealOutcome` gates on
              `closed_lost`, so a merged card renders no "why this deal was
              lost" panel, which is right — it was not lost. */}
          {mergedInto ? (
            <MergedIntoBanner
              onOpenCanonical={
                onOpenDeal && deal.duplicateOfDealId
                  ? () => openLinkedDeal(deal.duplicateOfDealId as string)
                  : undefined
              }
            />
          ) : null}

          {/* SUPERSEDED BY A NEWER QUESTIONNAIRE (S4.5) — above the tabs for the
              merged banner's reason (the work is on another card), and in its
              OWN words: it is not a staff duplicate and its undo is not the
              duplicate release. The ADMIN reopen rides the banner because it is
              the one way back; a clinician gets the sentence alone (R41 — the
              server's `@Roles('ADMIN')` is the control). */}
          {superseded ? (
            <SupersededBanner
              onOpenNewer={
                onOpenDeal && deal.supersededByDealId
                  ? () => openLinkedDeal(deal.supersededByDealId as string)
                  : undefined
              }
              onReopen={
                role === 'ADMIN'
                  ? () => {
                      setReopenError(null);
                      setReopenNotice(null);
                      setReopenOpen(true);
                    }
                  : undefined
              }
            />
          ) : null}
          {/* Mounted with a SUPERSEDED card's banner and filled after a reopen —
              a live region inserted with its words already inside is not
              reliably announced (WCAG 4.1.3). Only there: every other drawer
              has no reopen, and an empty status region on it is noise (and one
              more polite region on a surface that counts them). It stays
              mounted after the reopen, when the card stops being superseded,
              because that is exactly when it speaks. */}
          {superseded || reopenNotice ? (
            <p role="status" style={{ margin: reopenNotice ? '0 0 var(--gps-s-3)' : 0, fontSize: '13.5px', color: 'var(--gps-success-text)' }}>
              {reopenNotice}
            </p>
          ) : null}

          {/* See the `money` block above for the ADMIN gate and the
              both-fields-or-nothing rule. */}
          {monthlyPence !== null && planMonths !== null ? (
            <div
              /* ⚠️ A GROUPED DIV, NOT A <dl> (2026-09-06). The first draft was a
                 definition list, which reads perfectly as markup and fails an
                 accessibility scan the moment you name it: `<dl>` has no
                 implicit role, so an `aria-label` on it labels nothing, and
                 adding `role="group"` to fix that OVERRIDES the list semantics
                 its own `<dt>`/`<dd>` children require — every one of them
                 becomes a `dlitem` violation with no list to belong to. Caught
                 by the estate-wide axe sweep in admin/callbacks.spec.tsx, which
                 is why that sweep exists. Two labelled figures need a name and
                 a boundary, not a list. */
              role="group"
              aria-label="Plan price"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--gps-s-4)',
                margin: '0 0 var(--gps-s-3)',
                padding: 'var(--gps-s-3)',
                border: '1px solid var(--gps-border)',
                borderRadius: 'var(--gps-r-md)',
                background: 'var(--gps-surface-2)',
              }}
            >
              <div>
                <div style={{ fontSize: '12px', color: 'var(--gps-muted)' }}>First month</div>
                <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gps-text)' }}>
                  {formatPence(monthlyPence)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--gps-muted)' }}>Plan value</div>
                <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gps-text)' }}>
                  {formatPence(monthlyPence * planMonths)}
                </div>
                {/* The working, in full — this product is the portal's, not
                    the server's. See the `money` block for why it is shown. */}
                <div style={{ fontSize: '12px', color: 'var(--gps-muted)' }}>
                  {planMonths} {planMonths === 1 ? 'month' : 'months'} &times; {formatPence(monthlyPence)}
                </div>
              </div>
              {/* ⚠️ THE STATE, BESIDE THE FIGURE AND NOT A TAB AWAY. A refunded
                  or cancelled case still prints its worth — an admin wants to
                  know that — but must never read it as money we are holding.
                  The Payments tab puts `Order state` above the amount for the
                  same reason; this block hoists the amount, so the qualifier
                  has to come with it. */}
              {moneyState ? (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--gps-muted)' }}>Order state</div>
                  <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gps-text)' }}>{moneyState}</div>
                </div>
              ) : null}
            </div>
          ) : null}

          <Tabs label="Deal sections" tabs={tabs} activeId={tab} onChange={setTab} idBase={TAB_ID_BASE} />

          <TabPanel idBase={TAB_ID_BASE} tabId="overview" activeId={tab}>
            {/* THE OUTCOME BLOCK (stage clarity §1): on a closed deal this is
                the headline fact, so it leads the panel — and it REPLACES the
                bare "Clinical status" chip below. Absent (an open deal, or an
                older clinic-service) → the chip stays exactly as it was.
                2026-08-27: the chip no longer mis-attributes a hard stop (it
                derives nothing for one), so this is no longer a correction —
                it is a de-duplication. The block says the reason AND the
                author; the chip could only ever say "Rejected". */}
            {outcome ? (
              <DealOutcomeBlock outcome={outcome} actor={actorDirectory[outcome.closedByUserId ?? '']} />
            ) : null}
            {/* WHY THIS DEAL HAS STOPPED, in words, at the top of the panel
                everybody opens first (2026-09-12).

                The chip in the header says WHICH state it is in; this says what
                happened and what to do about it. Both matter and neither is the
                other: a reader who has never met the flag before learns nothing
                from "Re-approval needed" on its own, and the header has no room
                for a sentence. Actionable words, never an error code — see
                `AWAITING_REASON` / `REAPPROVAL_REASON`. */}
            <PrescribingReasonNotice flags={prescribingFlags} style={{ marginBottom: 'var(--gps-s-4)' }} />
            {/* A CLINICAL reason for not paying, on a deal still waiting on the
                patient (T-2, 2026-09-11): the one drop-off answer that changes
                WHO calls, so it leads the panel rather than waiting in a row.
                Once the patient has paid or gone it is history, and only the
                row below says it. */}
            {(() => {
              const dropOff = dropOffChipFor(deal, false);
              return dropOff?.clinical ? <DropOffClinicalCallout reason={dropOff.reason} /> : null;
            })()}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-4)' }}>
              <div>
                <SectionTitle>Contact</SectionTitle>
                <Row label="Email" value={detail.deal.contact.email} />
                <Row label="Phone" value={detail.deal.contact.phone ?? '—'} />
                <Row label="Entered via" value={ENTRY_LABEL[detail.deal.entryPath]} />
                {/* "First seen" and "In this stage" BOTH MOVED TO THE HEADER
                    (2026-09-06) — see `stageAge`. They are not deleted facts:
                    the frozen header now states the stage age and the creation
                    date on one line, where they stay readable from every tab
                    instead of only this one. Repeating them here would put the
                    same two dates on screen twice at once. */}
                {detail.deal.scheduledCallbackAt ? (
                  // Wave 9 §5 — a time block on new deals; legacy instants
                  // and free text still render readably (formatCallWindow).
                  <Row label="Call requested" value={formatCallWindow(detail.deal.scheduledCallbackAt)} />
                ) : null}
                {/* Marketing consent (2026-09-11) — beside the address it governs.
                    Its own read: a failure there says "unavailable" in this
                    section and never takes the drawer down with it. */}
                <MarketingConsentSection dealId={deal.dealId} role={role} />
              </div>
              <div>
                <SectionTitle>Progress</SectionTitle>
                {/* §9 — the DEAL's round position (both ledgers, server-derived);
                    the ticket row below stays the ticket-machinery's own count. */}
                {roundAttempts !== null ? (
                  <Row
                    label="This round"
                    value={`${roundAttempts} of ${REQUIRED_ROUND_ATTEMPTS} call attempts · ${deal.attemptsLifetime ?? roundAttempts} lifetime`}
                  />
                ) : null}
                {detail.callback ? (
                  <>
                    <Row label="Callback ticket" value={detail.callback.status.replace(/_/g, ' ')} />
                    {detail.callback.preferredTime ? (
                      // The PATIENT's asked-for window from the callback form
                      // (2026-08-26 — captured since launch but never shown to
                      // staff; the "Call requested" row above is the STAFF-set
                      // schedule, a different fact). formatCallWindow keeps a
                      // Wave-9 block, a legacy instant and legacy free text all
                      // readable.
                      <Row label="Patient asked for" value={formatCallWindow(detail.callback.preferredTime)} />
                    ) : null}
                    <Row
                      label="Call attempts"
                      value={`${detail.callback.attempts} total · ${detail.callback.attemptsThisRound} this round`}
                    />
                  </>
                ) : null}
                {detail.assessment ? (
                  <>
                    <Row label="Questionnaire" value={detail.assessment.state.replace(/_/g, ' ')} />
                    {/* §3 (2026-08-26): the icon-led clinical status chip
                        replaces the raw "Eligibility: clinician review" line
                        wherever one of the four walkthrough states applies —
                        FULL text stays visible beside the icon on the drawer
                        (WCAG: never colour/icon alone). Anything else keeps
                        the plain-text row as the fallback.
                        2026-08-27 (stage clarity §1): SUPPRESSED ENTIRELY when
                        the outcome block above is showing — it would repeat a
                        worse version of a fact the block has already stated in
                        full, with the author attached. This is now the ONLY
                        reason for the suppression: since the chip-truth fix the
                        derivation is honest at source (a hard stop derives no
                        chip at all), so this gate is de-duplication, NOT a
                        second guard papering over a bad derivation. */}
                    {outcome ? null : (() => {
                      const status = clinicalStatusFor(deal);
                      return status ? (
                        <Row label="Clinical status" value={<ClinicalStatusChip status={status} variant="full" />} />
                      ) : (
                        <Row label="Eligibility" value={detail.assessment.eligibility?.replace(/_/g, ' ') ?? 'not decided'} />
                      );
                    })()}
                    {detail.assessment.submittedAt ? (
                      <Row label="Submitted" value={formatUtcDate(detail.assessment.submittedAt)} />
                    ) : null}
                    {/* PHOTO CHASES (CTO 2026-08-27) — ADMIN only: chasing a
                        patient for their photo is back-office work, and the
                        clinician's surface already says "Awaiting patient
                        photo" where it matters (the Review tab). Rendered only
                        once a chase has actually gone out, so a normal journey
                        carries no extra row. */}
                    {role === 'ADMIN' && photoChases > 0 ? (
                      <Row label="Photo chased" value={`${photoChases} ${photoChases === 1 ? 'time' : 'times'}`} />
                    ) : null}
                  </>
                ) : null}
                {/* Why they haven't paid (T-2, 2026-09-11) — ALWAYS, when the
                    patient gave a reason: the drawer is where history is read,
                    so a patient who said "the cost" and then paid still says so
                    here. Absent when no reason was recorded — the usual case. */}
                {(() => {
                  const line = dropOffDrawerText(deal);
                  return line ? <Row label="Why they haven’t paid" value={line} /> : null;
                })()}
                {!detail.callback && !detail.assessment ? (
                  // A FRESH LEAD IS A PROMPT, NOT A DEAD END (2026-08-29).
                  // The old line described the record and stopped. Both roles
                  // have a real next move here and they are different ones, so
                  // the copy splits: a clinician can start the questionnaire
                  // from the tab next door, and anyone can log the call they
                  // are probably on right now.
                  <Help>
                    No callback or questionnaire yet — a fresh lead.{' '}
                    {role === 'CLINICIAN'
                      ? 'Start one from the Questionnaire tab, or log a call below.'
                      : 'Use Log activity at the bottom of this panel to record a call or a note.'}
                  </Help>
                ) : null}
              </div>
            </div>

            {/* CHASED TWICE AND STILL NOTHING (CTO 2026-08-27): surface it to
                an ADMIN and let a human decide — NEVER auto-close. Quiet by
                design (role="status", not an alert): this is a follow-up
                prompt on a back-office screen, not a clinical warning, and the
                one assertive region on the drawer stays where it belongs.
                Keyed on "chased ≥ 2 AND the gallery is empty" — an absent
                `photos` key (an older clinic-service) means UNKNOWN, and a
                prompt to chase someone who may already have uploaded is worse
                than no prompt at all. */}
            {photoFollowUpDue ? (
              <Alert tone="warning" role="status" style={{ marginTop: 'var(--gps-s-4)' }}>
                Chased {photoChases} times for a weight photo and none has been uploaded — follow this
                up with the patient by hand. Nothing closes automatically.
              </Alert>
            ) : null}

            {/* ── THE PATIENT JOURNEY (CTO 2026-09-07) ─────────────────────
                One rail for the whole route, with the OUTSTANDING STEP and the
                controls that perform it pinned to its top.

                WHY IT SITS HERE — high in the tab, under the contact block and
                above the bookkeeping. Every fact on it was already in this
                drawer and that was the problem: the questionnaire's progress
                was behind one tab, the clinical decision behind another, the
                money behind a third that clinicians cannot open at all, and the
                pharmacy tail behind a fourth. "What is this patient waiting
                for?" took four tabs and the knowledge of which order they
                happened in.

                ⚠️ IT ALSO CARRIES THE PLAN LINK NOW. The "Plan link" section
                that used to stand at the FOOT of this tab is gone — not
                deleted, MOVED, and it is still exactly one mount of exactly the
                same component with exactly the same gate (see
                `planLinkAvailable` in journeyModel.ts, which copies the old
                condition verbatim rather than re-deriving it). The single-home
                rule from 2026-09-05 is intact; the home is better, because the
                control now sits inside the sentence that says why to press it.

                BOTH STAFF ROLES. The panel is money-free for a clinician by
                construction — see the money note in JourneyPanel.tsx. */}
            {/* ── CARD AND PRE-AUTHORISATION (CTO 2026-09-11) ──────────────
                "When you open the detail card, you make a call… figure out
                whether it's valid or not." A LIVE Stripe read for this deal's
                order (orders-service `GET /v1/orders/:id/hold-status`), because
                the posture on the card comes off orders' ledger and still says
                "authorised" for a hold Stripe has already let go.

                WHY DIRECTLY ABOVE THE PATIENT JOURNEY, not under it or inside
                it. Under it is the far side of a rail that runs to a dozen
                rungs — below the fold, on the drawer the CTO wants this read
                on opening. Inside it would hand JourneyPanel a Stripe read of
                its own, when its contract is that the drawer owns every read
                except the questionnaire count. Here it sits beside the payment
                rungs it qualifies, and the journey's band is still the first
                thing the journey says.

                BOTH STAFF ROLES, money-free for a clinician twice over: their
                answer arrives with no pence key, and the panel refuses to print
                a figure for anyone but an ADMIN. It asks again whenever this
                tab mounts, so coming back to Overview after acting on the
                Review tab shows the hold as it stands now. */}
            <HoldStatusPanel deal={deal} role={role} />
            {/* ── JOURNEY / ENGAGEMENT — nested tabs (2026-09-23) ──────────
                The Patient journey panel gained a sibling: the Engagement view
                (the re-engagement email sequence + the sent-items timeline +
                the ADMIN send/WhatsApp lanes). A NESTED Tabs group, its own
                idBase (the InsightsSection precedent), Journey the default —
                and the Journey tab is the EXISTING JourneyPanel mounted
                byte-identically: same component, same props, same single
                mount. Both staff roles see both tabs; every control inside
                Engagement that reaches the patient is ADMIN-only affordance
                over an ADMIN-only route (R41). */}
            {/* RETURNING PATIENT (S4.5) — the chips again, with the contact's
                OTHER open cards, which is what makes "Two live cards" and
                "Started again" something a desk can act on. Renders nothing for
                a card with no returning block (an older clinic) or nothing to
                say. */}
            <ReturningSummary card={deal} onOpenDeal={onOpenDeal ? openLinkedDeal : undefined} />
            <div style={{ marginTop: 'var(--gps-s-4)' }}>
              {showsEngagementView(role) ? (
                <>
                  <Tabs
                    label="Journey and engagement"
                    tabs={OVERVIEW_VIEW_TABS}
                    activeId={overviewView}
                    onChange={(id) => setOverviewView(id as OverviewView)}
                    idBase={OVERVIEW_VIEW_ID_BASE}
                  />
                  <TabPanel idBase={OVERVIEW_VIEW_ID_BASE} tabId="journey" activeId={overviewView}>
                    <JourneyPanel
                      deal={deal}
                      detail={detail}
                      /* The StaffOrder this drawer already fetched once for the Review
                         and Payments tabs — the panel never issues that read itself.
                         'missing' means we looked and the queue had no such case, which
                         is a null to a consumer that only wants the fulfilment tail. */
                      order={reviewOrder && reviewOrder !== 'missing' ? reviewOrder : null}
                      role={role}
                    />
                  </TabPanel>
                  <TabPanel idBase={OVERVIEW_VIEW_ID_BASE} tabId="engagement" activeId={overviewView}>
                    <EngagementPanel deal={deal} role={role} />
                  </TabPanel>
                </>
              ) : (
                /* CLINICIAN (S4.5, Q8): the SAME JourneyPanel, same props, and no
                   tab strip — there is only one view to show, and a one-tab strip
                   would be a control that does nothing. See showsEngagementView. */
                <JourneyPanel
                  deal={deal}
                  detail={detail}
                  order={reviewOrder && reviewOrder !== 'missing' ? reviewOrder : null}
                  role={role}
                />
              )}
            </div>

            {/* THE THIRD STATEMENT OF THE STAGE AGE USED TO BE HERE — a
                rotting `Alert` or a quiet `Help` spelling out the same integer
                the header chip and the Progress row above had already given.
                All three collapsed into the header's one line on 2026-09-06
                (see `stageAge`), rotting wording and all. Deliberately NOT
                replaced with a shorter version of itself: a third rendering of
                one number was the problem, not its length. */}

            {moves.length === 0 ? (
              <Alert tone="info" role="status" style={{ marginTop: 'var(--gps-s-4)' }}>
                {/* THREE reasons a card can have no manual move, and they are
                    not interchangeable. An AUTHORISED card in the clinical
                    queue is OPEN and moves on the clinician's decision (this
                    used to be the whole stage `awaiting_clinical_review`; since
                    the merge it is the row state, and since the authorisation
                    gate that row state is `holdAuthorisedAt`). A merged duplicate is closed but NOT
                    lost, and its way back is the un-merge, not a stage move —
                    calling it "terminal" would be true and useless. Everything
                    else really is a finished deal. */}
                {isAwaitingClinicalDecision(deal)
                  ? 'This deal moves on the clinical decision — see the Review tab.'
                  : superseded
                    ? /* S4.5: a SUPERSEDED card is not a staff duplicate, and
                         "un-mark the duplicate" was the wrong undo (the server
                         refuses the duplicate actions on it). It names the
                         ADMIN reopen instead — clinic's own recover() answer. */
                      SUPERSEDED_NO_MOVES
                    : deal.stage === 'closed_duplicate'
                      ? 'This deal was merged into another one. To work it again, un-mark the duplicate.'
                      : 'This deal is in a terminal stage.'}
              </Alert>
            ) : null}

            {/* (c) WHAT WAS MERGED INTO THIS CARD (2026-09-06, CTO — "the
                canonical shows what was merged into it").
                ON OVERVIEW, NOT ON HISTORY, and the History tab's "Previous
                deals" list is the obvious alternative that was rejected. Two
                reasons. Clinically, this is how somebody opening the surviving
                card learns there was a SECOND SUBMISSION by the same patient —
                that is a fact about the case in front of them, not archive
                material. Operationally, one of these rows can carry a live
                double charge, and the money must not be a tab away on the one
                card staff actually keep open. History still badges the merged
                siblings it already lists, so nothing is hidden from there. */}
            {mergedDuplicates.length > 0 ? (
              <div
                style={{
                  marginTop: 'var(--gps-s-4)',
                  paddingTop: 'var(--gps-s-3)',
                  borderTop: '1px solid var(--gps-border)',
                }}
              >
                <SectionTitle>
                  {mergedDuplicates.length === 1 ? 'Merged into this deal' : `Merged into this deal (${mergedDuplicates.length})`}
                </SectionTitle>
                <Help style={{ marginTop: 0 }}>
                  The same patient started more than once. These journeys were closed and folded into this
                  card.
                </Help>
                <MergedDuplicatesList
                  rows={mergedDuplicates}
                  /* Names, not ids — resolved by the drawer's ONE ADMIN-only
                     batched staff lookup (the merge actors join that batch
                     alongside the feed's). A CLINICIAN resolves nobody and the
                     row simply omits the "by …" clause, exactly as the audit
                     feed does: a raw UUID in a "who did this" slot reads to
                     staff as a system action. */
                  actorNames={Object.fromEntries(
                    Object.entries(actorDirectory).map(([id, resolved]) => [id, resolved.name]),
                  )}
                  onOpenDeal={openLinkedDeal}
                />
              </div>
            ) : null}

            {/* THE PLAN LINK USED TO END THIS TAB, and it has MOVED UP into
                the Patient journey panel's action band (CTO 2026-09-07) — same
                component, same single mount, same gate. It is not deleted and
                it did not gain a second home: the 2026-09-05 rule that this
                control exists exactly once for both staff roles still holds.
                What changed is that it now stands beside the sentence naming
                the outstanding step ("the patient must choose a plan and pay —
                no card was ever collected"), instead of at the bottom of a
                panel a desk had to scroll to and then infer the reason for. */}
          </TabPanel>

          {/* 2026-08-27 restructure: the COMMS TRAIL ONLY — the composer now
              lives in the footer's Log-activity modal, and the clinical-note
              thread moved to the Review tab (decide + document in one place).
              2026-08-27 (CTO drawer review): and now LOGGED ACTIVITY ONLY —
              notes, call attempts and logged emails a person actually sat down
              and recorded (LOGGED_KINDS). Everything the system wrote — the
              journey milestones, the stage moves, the system-sent emails —
              moved to the Audit log, where the CTO expected to find it.

              THE OUTSTANDING ASK is NOT re-rendered here on purpose. The stage-
              clarity DealActionBanner already renders it ABOVE the tab strip
              (and therefore above this panel) on EVERY tab — a strictly more
              prominent placement than inside one panel, and the documented
              reason it lives there is that an admin must see the ask without
              first knowing which tab to open. A second copy inside this panel
              would put the same Alert on screen twice. stage-clarity.spec.tsx
              §2 pins the behaviour instead of the placement: with an
              outstanding ask this tab leads with it, ahead of the logged feed;
              with none, no highlight renders. */}
          <TabPanel idBase={TAB_ID_BASE} tabId="activity" activeId={tab}>
            {notEligible ? (
              <NotEligibleBanner
                stage={deal.stage}
                context="Advise the caller; you can still log calls and notes with the Log activity button."
              />
            ) : null}
            {/* WAYFINDING for the rows this change relocated. Staff who knew
                the old tab will come looking for the journey and the stage
                moves; saying where they went costs one quiet line and saves the
                "the trail has been deleted" support ticket. A `Help`, not an
                Alert — nothing is wrong, this is a signpost. */}
            <Help style={{ marginBottom: 'var(--gps-s-3)' }}>
              Notes, calls and emails logged by the team. The journey and everything the system
              recorded — form submissions, stage moves, emails we sent — are on the Audit log tab.
            </Help>
            {/* THE PER-OWNER ATTEMPT TALLY (moved off the board card, CTO
                2026-09-06). It sits here, directly above the feed, because it
                is the SUMMARY OF THAT FEED: the same `call_attempt` rows listed
                below, counted by the side of the handover that made them. A
                reader can check the tally against the rows without changing
                tab, which is the one thing the Overview tab's two attempt
                numbers cannot offer — those count ROUNDS and TOTALS off the
                callback ticket, this counts DIALS BY OWNER off both ledgers.

                ABOVE the feed and BELOW the wayfinding Help on purpose: it is
                orientation for the list, not an item in it, and putting it
                under the rows would make it something you find only after
                scrolling the very list it was meant to summarise.

                Renders NOTHING when clinic-service has not sent the split (the
                additive [CONTRACTS GAP] posture) — never a board of invented
                zeroes claiming nobody phoned. */}
            <AttemptSummary card={deal} />
            {feedEntries.length === 0 ? (
              // NAMES THE CONTROL, AND WHERE IT IS (2026-08-29, CTO: "guide
              // properly… say you can log an activity here by clicking this").
              // "Nothing logged yet" stated an absence and stopped; a staff
              // member new to the drawer had no way to know the thing that
              // fills this list is a button in the footer. Safe to promise:
              // the Log activity button has no role gate and renders on every
              // deal including terminal ones (see the footer note above).
              <Help>
                Nothing logged yet. Use <strong>Log activity</strong> at the bottom of this panel to
                record a call, a note or an email you sent.
              </Help>
            ) : (
              <ol style={{ listStyle: 'none', margin: '0 0 var(--gps-s-3)', padding: 0, display: 'grid', gap: '8px' }} aria-label="Deal activity">
                {feedEntries.map((entry, i) => (
                  <ActivityRow
                    key={`${entry.at}-${i}`}
                    entry={entry}
                    actor={entry.actorUserId ? actorDirectory[entry.actorUserId] : undefined}
                    /* (d) both trails, both tabs. The duplicate rows are
                       `audit` kind so they land in the Audit log, but the props
                       ride BOTH mounts on purpose: the two lists share one row
                       renderer, and a future feed label that moved these rows
                       into Activity must not silently drop their links. */
                    duplicateLinks={duplicateTrailLinks}
                    onOpenDeal={openLinkedDeal}
                  />
                ))}
              </ol>
            )}
          </TabPanel>

          {showQuestionnaire ? (
            <TabPanel idBase={TAB_ID_BASE} tabId="questionnaire" activeId={tab}>
              <QuestionnairePanel
                deal={deal}
                assessment={detail.assessment}
                role={role}
                onChanged={() => {
                  load();
                  onChanged();
                }}
                onStopped={() => {
                  // Soft detail refresh: 13–17 hard stops move the deal to Lost
                  // and we want the header/eligibility to update. CLIN-04 under-13
                  // purge deletes the deal (404) — keep the hard-stop dialog on
                  // screen instead of swapping to a load-error state. Transient
                  // detail faults are also ignored here so the stop UI stays.
                  getDealDetail(listCard.dealId)
                    .then((d) => {
                      setDetail(d);
                      setStatus('ready');
                    })
                    .catch(() => undefined);
                  onChanged();
                }}
              />
              {/* H5 (2026-08-27 review fix): the general assessment-notes
                  thread on a DRAFT assessment. The 2026-08-27 restructure made
                  the Review tab the thread's single home — but Review only
                  exists once the assessment is submitted (or an order case
                  exists), so a draft lost ALL access to it: the clinician
                  filling the form on a caller's behalf, and a CLIN-03
                  stopped-while-draft deal, both need to read AND add to the
                  running clinical record in-drawer. Mounted here ONLY while
                  the Review tab is absent (gated on !showReviewTab, not on
                  assessment state, so the two mounts can never coexist);
                  same store, composer and author-only edit rule, and the
                  default "Clinical notes" name — this is the only notes
                  thread on the tab, so no disambiguation is needed. */}
              {detail.assessment && !showReviewTab ? (
                <div style={{ marginTop: 'var(--gps-s-4)' }}>
                  <AssessmentNotesSection assessmentId={detail.assessment.assessmentId} />
                </div>
              ) : null}
            </TabPanel>
          ) : null}

          {showReviewTab ? (
            <TabPanel idBase={TAB_ID_BASE} tabId="review" activeId={tab}>
              {/* CLIN-03 — the Review tab is now reachable for order-less submitted
                  assessments, including sticky not_eligible ones; name the Lost
                  outcome here too, like every other working tab. */}
              {notEligible ? (
                <NotEligibleBanner
                  stage={deal.stage}
                  context="The review below is for your records; there are no actions to take."
                />
              ) : null}
              {deal.orderCaseRef ? (
                reviewOrder === null ? (
                  <DrawerSkeleton blocks={2} label="Loading the case…" />
                ) : reviewOrder === 'missing' ? (
                  <Alert tone="warning" role="status">
                    The order case behind this deal could not be found in the queue.
                  </Alert>
                ) : (
                  <ReviewView
                    order={reviewOrder}
                    onBack={() => setTab('overview')}
                    recoveredNotice={actionNotice}
                    onActioned={(notice) => {
                      if (notice) setActionNotice(notice);
                      load();
                      onChanged();
                    }}
                    // The SAME answer that flagged the tab — one read, so the
                    // marker and the band can never tell different stories.
                    safety={safety.view}
                    onRetrySafety={safety.retry}
                    // §2 (2026-08-26): when the patient agreed — from the deal
                    // detail (the drawer already holds it; the review read has
                    // no timestamp). Absent renders nothing.
                    consentAgreedAt={detail.assessment?.consentAgreedAt ?? null}
                    // …and WHICH WORDING those ticks were against (clinic #166):
                    // the weight declaration is only reconstructable from its
                    // version, so the version travels with the timestamp.
                    consentQuestionSetVersion={detail.assessment?.consentQuestionSetVersion ?? null}
                  />
                )
              ) : (
                // Order-less submitted assessment — the full review, decisions gated off (item 2).
                <ReviewView
                  assessmentId={deal.assessmentId ?? undefined}
                  onBack={() => setTab('overview')}
                  recoveredNotice={actionNotice}
                  onActioned={(notice) => {
                    if (notice) setActionNotice(notice);
                    load();
                    onChanged();
                  }}
                  // §2 — same consent timestamp on the order-less mount, and the
                  // same wording version beside it.
                  consentAgreedAt={detail.assessment?.consentAgreedAt ?? null}
                  consentQuestionSetVersion={detail.assessment?.consentQuestionSetVersion ?? null}
                />
              )}
            </TabPanel>
          ) : null}

          {role === 'ADMIN' && deal.orderCaseRef ? (
            <TabPanel idBase={TAB_ID_BASE} tabId="payments" activeId={tab}>
              {reviewOrder === null ? (
                <DrawerSkeleton blocks={1} label="Loading payment details…" />
              ) : reviewOrder === 'missing' ? (
                <Alert tone="warning" role="status">
                  The order case behind this deal could not be found in the queue.
                </Alert>
              ) : (
                <>
                  <Row label="Order state" value={reviewOrder.state.replace(/_/g, ' ')} />
                  {/* The order's PLAN, split into its two real facts and
                      labelled honestly. It used to render
                      `${productKey ?? '—'} × ${months ?? '—'} months`, which was
                      wrong twice: an em-dash is indistinguishable from a
                      hyphen, a loading state and a deliberate blank, and the
                      raw product key sat under a "Plan" label as though it were
                      a medication name. The staff queue carries no medication
                      detail at all (StaffOrder has productKey and months and
                      nothing else) — the prescribed lines live on the
                      prescriptions read, which the clinician review renders in
                      full. So this says what it has, flags what it has not, and
                      claims nothing. */}
                  <Row label="Plan (product key)" value={reviewOrder.productKey ?? <NotRecorded label="product key" />} />
                  <Row
                    label="Term"
                    value={typeof reviewOrder.months === 'number' ? `${reviewOrder.months} months` : <NotRecorded label="term" />}
                  />
                  {typeof reviewOrder.monthlyPence === 'number' ? (
                    <Row label="Monthly" value={`£${(reviewOrder.monthlyPence / 100).toFixed(2)}`} />
                  ) : null}
                  <Help style={{ marginTop: 'var(--gps-s-2)' }}>
                    Full money view (lifetime value, secured vs estimated) lives on the Payments section.
                  </Help>
                </>
              )}
            </TabPanel>
          ) : null}

          {/* UX Wave 9 §1 — the deal-scoped audit trail as a TAB (floated
              right in the row above), reusing the exact ActivityRow
              rendering/sanitisation rules.
              2026-08-27 (CTO drawer review): now the FULL system/journey trail,
              not just `kind === 'audit'` — the COMPLEMENT of the Activity tab's
              logged kinds. Mini form submitted, questionnaire started and
              submitted, callback requested, every stage transition, the emails
              the platform sent, the deal being created: all of it lands here
              because none of it is something a person logged.
              NO ROLE GATE, unchanged: this tab was visible to CLINICIAN and
              ADMIN before this change and still is. Widening OR narrowing who
              can read the trail is not this fix's business — a clinician
              needs the journey to work the deal, and the data is the same
              already-authorised aggregate the drawer read. (Actor NAMES stay
              ADMIN-only, as they were: only an admin's batched staff lookup
              resolves them.) */}
          <TabPanel idBase={TAB_ID_BASE} tabId="audit" activeId={tab}>
            {auditEntries.length === 0 ? (
              // DELIBERATELY OFFERS NO ACTION. Audit rows are written by the
              // SYSTEM — nobody can add one, so inviting the reader to do
              // something here would be a lie. What it can usefully say is why
              // the list is empty and where the human-logged trail lives, which
              // is the question someone staring at an empty audit tab actually
              // has.
              <Help>
                Nothing recorded yet. This tab fills itself as the journey happens — form
                submissions, stage moves and emails we sent. Notes and calls your team logs are on
                the Activity tab.
              </Help>
            ) : (
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }} aria-label="Deal audit entries">
                {auditEntries.map((entry, i) => (
                  <ActivityRow
                    key={`${entry.at}-${i}`}
                    entry={entry}
                    actor={entry.actorUserId ? actorDirectory[entry.actorUserId] : undefined}
                    /* (d) both trails, both tabs. The duplicate rows are
                       `audit` kind so they land in the Audit log, but the props
                       ride BOTH mounts on purpose: the two lists share one row
                       renderer, and a future feed label that moved these rows
                       into Activity must not silently drop their links. */
                    duplicateLinks={duplicateTrailLinks}
                    onOpenDeal={openLinkedDeal}
                  />
                ))}
              </ol>
            )}
          </TabPanel>

          <TabPanel idBase={TAB_ID_BASE} tabId="history" activeId={tab}>
            {notEligible ? (
              <NotEligibleBanner
                stage={deal.stage}
                context="Advise the caller; earlier deals for this contact still show below."
              />
            ) : null}
            {/* ── THE ROUTE-BACKED VIEW (S4.5) ─────────────────────────────
                `GET …/deals/:id/history`, DARK on every environment today: a
                404 (`disabled`, or a clinic-service without the route) HIDES
                it, and the tab keeps exactly the lists below that it always
                had. While it loads, and if it fails, the old lists stay too —
                an outage must never read as "nothing earlier". Only a READY
                answer replaces "Previous attempts", which it supersedes (the
                same rows, each with how it was matched, and the logged reveal). */}
            {history.load.state === 'ready' ? (
              <ContactHistoryView
                dealId={deal.dealId}
                history={history.load.history}
                revealed={history.revealed}
                onRevealed={history.remember}
                role={role}
                onOpenDeal={onOpenDeal ? openLinkedDeal : undefined}
              />
            ) : history.load.state === 'loading' ? (
              <p role="status" style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
                Checking for earlier questionnaires…
              </p>
            ) : history.load.state === 'error' ? (
              <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
                We could not load earlier questionnaires just now. This does not mean there are none.{' '}
                <Button type="button" variant="ghost" style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }} onClick={history.retry}>
                  Try again
                </Button>
              </Alert>
            ) : null}
            {/* Previous attempts FIRST: on a repeat this is the only place the
                earlier try still shows, and `otherDeals` below is empty for
                every repeat since the board stopped minting sibling cards.
                `?? []` because clinic-service deploys separately — an older API
                simply has nothing to show here, it must not break the tab.
                (S4.5) Withheld only when the route-backed view above is READY. */}
            {history.load.state !== 'ready' && (detail.priorAttempts ?? []).length > 0 ? (
              <>
                <SectionTitle>Previous attempts</SectionTitle>
                <ul aria-label="Previous attempts" style={{ listStyle: 'none', margin: '0 0 var(--gps-s-3)', padding: 0, display: 'grid', gap: '8px' }}>
                  {(detail.priorAttempts ?? []).map((attempt) => (
                    <PriorAttemptRow key={`${attempt.kind}-${attempt.ref}`} attempt={attempt} />
                  ))}
                </ul>
                <SectionTitle>Previous deals</SectionTitle>
              </>
            ) : history.load.state === 'ready' ? (
              <SectionTitle>Previous deals</SectionTitle>
            ) : null}
            {detail.otherDeals.length === 0 ? (
              history.load.state === 'ready' ? (
                <Help>No other deal cards for this contact.</Help>
              ) : (detail.priorAttempts ?? []).length === 0 ? (
                <Help>Nothing earlier — this is the contact&rsquo;s first time through.</Help>
              ) : (
                <Help>No other deal cards — every attempt above sits on this one.</Help>
              )
            ) : (
              <>
              {/* THE BASIS, SAID ONCE ABOVE THE LIST (2026-09-26, S4.5 review).
                  Every row here is another card on the SAME CONTACT, and clinic
                  keeps one contact per email address (`@@unique([tenantId,
                  email])`; siblings are `contactId`-keyed) — so each one is an
                  email match, and a shared inbox can put a stranger's card
                  here. The History view above withholds the card link from
                  email-only rows and warns before a logged reveal; these rows
                  open with one click and no audit row (the same as opening any
                  board card), so the least they owe the reader is the same
                  words. [CONTRACTS GAP] clinic's `dealSibling` carries no match
                  basis: when it does, a proven row can drop the words. Until
                  then EVERY row is labelled, never a guess at which is proven.
                  The DP note's "Ways round the logged click" already lists this
                  list as a route to the earlier card. */}
              <p id={SIBLING_BASIS_ID} style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--gps-warn-text)', fontWeight: 600 }}>
                {SIBLING_BASIS_NOTE}
              </p>
              <ul aria-label="Previous deals" aria-describedby={SIBLING_BASIS_ID} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
                {detail.otherDeals.map((sibling) => (
                  <li key={sibling.dealId} style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <span>
                      {/* THE ONE LIST A MERGED CARD IS STILL SHOWN IN.
                          clinic-service deliberately does not filter duplicates
                          out of `otherDeals`, so without the marker this row
                          says a sibling is closed and cannot say it was folded
                          into the card being read. `duplicateOfDealId` is
                          optional here for the usual deploy-window reason —
                          absent renders nothing, never "not a duplicate". */}
                      {sibling.duplicateOfDealId != null ? (
                        <span style={{ marginRight: '6px' }}>
                          <DuplicateMarker />
                        </span>
                      ) : null}
                      {/* S4.5: a sibling the SYSTEM closed for a newer
                          questionnaire says so in its own word — a
                          `closed_duplicate` row with no staff pointer used to
                          read as a duplicate nobody could explain. */}
                      {sibling.supersededByDealId != null ? (
                        <span style={{ marginRight: '6px' }}>
                          <SupersededMarker />
                        </span>
                      ) : null}
                      <Badge tone={toneFor(sibling.stage)}>{STAGE_LABEL[sibling.stage]}</Badge>
                      <span style={{ marginLeft: '8px', fontSize: '13px', color: 'var(--gps-muted)' }}>
                        {ENTRY_LABEL[sibling.entryPath]}
                      </span>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--gps-muted)' }}>
                      {formatUtcDate(sibling.createdAt)}
                      {/* REACHABLE (S4.5): the old card behind an "Already on an
                          active plan" chip is a sibling here, and this is the
                          click through to it (Addendum 8 — S6.2 puts the
                          Change-treatment cancel on that old card). The id was
                          already on the wire; the row just never offered it. */}
                      {onOpenDeal ? (
                        <DealLinkButton
                          onClick={() => openLinkedDeal(sibling.dealId)}
                          label={`Open the ${STAGE_LABEL[sibling.stage] ?? sibling.stage} deal started on ${formatUtcDate(sibling.createdAt)} (${SIBLING_BASIS_LABEL})`}
                        >
                          Open
                        </DealLinkButton>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
              </>
            )}
          </TabPanel>
        </>
      )}
    </Drawer>
    {/* Recover-lead confirm — a REQUIRED reason (ConfirmDialog gates confirm
        behind it) lands in the server audit trail. The 409/error message shows
        in the dialog body. */}
    <ConfirmDialog
      open={recoverOpen}
      title={`Recover ${dealName(deal)} into the pipeline`}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            This brings the lead back into an active stage (Checking eligibility). Give a reason — it is
            recorded on the deal.
          </p>
          {recoverError ? (
            <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
              {recoverError}
            </span>
          ) : null}
        </>
      }
      confirmLabel={recoverBusy ? 'Recovering…' : 'Recover lead'}
      busy={recoverBusy}
      requireReason
      reasonLabel="Reason for recovering"
      onConfirm={(reason) => void runRecover(reason)}
      onCancel={() => setRecoverOpen(false)}
    />
    {/* Reopen a superseded card (S4.5, ADMIN) — the Recover-lead grammar: a
        REQUIRED reason that lands on the server's `deal_supersede_released`
        audit row, and a refusal kept inside the open dialog. */}
    <ConfirmDialog
      open={reopenOpen}
      title={`Reopen ${dealName(deal)}'s older card`}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            This card was closed because the patient submitted a newer questionnaire. Reopen it only if the two are
            separate journeys — it goes back to the lane it was closed from, and the patient will then have two open
            cards. Give a reason; it is recorded on the card.
          </p>
          {reopenError ? (
            <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
              {reopenError}
            </span>
          ) : null}
        </>
      }
      confirmLabel={reopenBusy ? 'Reopening…' : 'Reopen card'}
      busy={reopenBusy}
      requireReason
      reasonMaxLength={2000}
      reasonLabel="Reason for reopening"
      onConfirm={(reason) => void runReopenSuperseded(reason)}
      onCancel={() => setReopenOpen(false)}
    />
    {/* Send-for-admin-review — the SAME ConfirmDialog + required-reason pattern
        as Recover lead above, deliberately not a new dialog. The note is the
        whole point of the handoff (a handoff with no "why" is unactionable), so
        the dialog gates confirm behind it and the server requires it too. */}
    <ConfirmDialog
      open={handoffOpen}
      title={`Send ${dealName(deal)} for admin review`}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            The deal moves to Pending admin review for an admin to action, and your note goes on the deal timeline.
            Nothing about the patient&rsquo;s plan changes here &mdash; stopping or changing a plan is an admin action.
          </p>
          {handoffError ? (
            <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
              {handoffError}
            </span>
          ) : null}
        </>
      }
      confirmLabel={handoffBusy ? 'Sending…' : 'Send for admin review'}
      busy={handoffBusy}
      requireReason
      reasonLabel="What does the admin need to action?"
      onConfirm={(note) => void runSendForAdminReview(note)}
      onCancel={() => setHandoffOpen(false)}
    />
    {/* Mark-as-test-record confirm — the SAME ConfirmDialog grammar as the two
        above, with `optionalReason` (the server takes the mark without one; a
        note still lands on the audit row when given). The partial-failure
        message renders in the dialog body so retry happens where the action
        started. */}
    <ConfirmDialog
      open={markTestOpen}
      title={`Mark ${dealName(deal)} as a test record`}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            This closes the deal as a test record
            {markOrderLink?.state === 'found' ? ' and flags its linked order' : ''} so reporting excludes{' '}
            {markOrderLink?.state === 'found' ? 'them' : 'it'}, and the card disappears from the pipeline
            board (find it again under “Show test records”). Nothing is deleted and no email is sent.
          </p>
          {/* ⚠️ THE ORDER HALF, SAID OUT LOUD EITHER WAY (2026-09-04).
              A silent "no order here" is what put three test transactions on the
              admin Payments board looking like £565.95 of real revenue each: the
              old copy simply dropped the clause when `orderCaseRef` was null,
              and `orderCaseRef` is null for every deal that was terminal or
              parked when checkout landed. So the dialog now states which of the
              two halves it is about to do — and when it cannot name an order, it
              says so and names the one surface that can finish the job (item 3's
              mark on the Payments board). The `checking` line is not decoration:
              confirm is withheld while it shows, because the sentence above it
              is not yet true. */}
          {markOrderLink?.state === 'checking' ? (
            <p role="status" style={{ margin: '0 0 8px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
              Checking whether this deal has a linked order…
            </p>
          ) : null}
          {markOrderLink?.state === 'none' ? (
            <p style={{ margin: '0 0 8px', fontSize: '13.5px', color: 'var(--gps-text)' }}>
              <strong>No linked order was found</strong>, so nothing on the payments boards changes. If this
              patient does have an order, an admin has to mark it on the Payments board as well — otherwise
              it keeps counting as real revenue.
            </p>
          ) : null}
          {markTestError ? (
            <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
              {markTestError}
            </span>
          ) : null}
        </>
      }
      confirmLabel={markTestBusy ? 'Marking…' : markTestOrderDone ? 'Retry deal marking' : 'Mark as test record'}
      busy={markTestBusy}
      // The copy above is the reason: while the order lookup is in flight the
      // dialog cannot yet say which halves this press will do.
      confirmDisabled={markOrderLink?.state === 'checking'}
      optionalReason
      reasonLabel="Why is this a test record? (optional)"
      onConfirm={(reason) => void runMarkTest(reason)}
      onCancel={() => {
        setMarkTestOpen(false);
        setMarkOrderLink(null);
      }}
    />
    {/* Unmark-test-record confirm (2026-08-26 §5) — the SAME ConfirmDialog
        grammar with `optionalReason`, mirroring the mark dialog above. The
        copy tells the TRUTH about the server revert (clinic#137, flag-only):
        the deal keeps whatever stage it is in — it never claims the card
        "returns to the working board", because a mark-closed deal stays Lost
        until the separate Recover lead action reopens it. */}
    <ConfirmDialog
      open={unmarkOpen}
      title={`Unmark ${dealName(deal)} as a test record`}
      body={
        <>
          <p style={{ margin: '0 0 8px' }}>
            This clears the test flag, so reporting counts the deal as a real record again. The deal
            keeps its current stage — if it was closed as a test record it stays closed, and
            reopening it is the separate &ldquo;Recover lead&rdquo; action.
          </p>
          {unmarkError ? (
            <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
              {unmarkError}
            </span>
          ) : null}
        </>
      }
      confirmLabel={unmarkBusy ? 'Unmarking…' : 'Unmark test record'}
      busy={unmarkBusy}
      optionalReason
      reasonLabel="Why is this a real record? (optional)"
      onConfirm={(reason) => void runUnmarkTest(reason)}
      onCancel={() => setUnmarkOpen(false)}
    />
    {/* THE DUPLICATE PAIR'S TWO DIALOGS (2026-09-06) — the picker and the
        release. Mounted off ONE `dupMode` because a card is either merged or it
        is not, so both can never be open. They own their own copy (see
        MarkDuplicateDialog.tsx): the money sentence in particular is not
        repeated here, so there is exactly one place it can go wrong. */}
    {dupMode === 'mark' ? (
      <MarkDuplicateDialog
        deal={deal}
        busy={dupBusy}
        error={dupError}
        onCancel={() => setDupMode(null)}
        onConfirm={(canonicalDealId, reason) => void runMarkDuplicate({ canonicalDealId }, reason)}
      />
    ) : null}
    {dupMode === 'release' ? (
      <ReleaseDuplicateDialog
        deal={deal}
        busy={dupBusy}
        error={dupError}
        onCancel={() => setDupMode(null)}
        onConfirm={(reason) => void runMarkDuplicate({ revert: true }, reason)}
      />
    ) : null}
    {/* WHAT THE CASCADE DID (2026-09-06) — the answer dialog, mounted after the
        confirm above has closed. It renders ONLY when the cascade has something
        to say, so the ordinary clean mark is unchanged. `openLinkedDeal` is the
        same in-drawer navigation the merged-duplicate rows use, which is what
        turns the withheld leg's "mark those deals too" from an instruction into
        a route. */}
    {cascadeReport ? (
      <MarkTestCascadeDialog
        dealName={dealName(deal)}
        direction={cascadeReport.direction}
        cascade={cascadeReport.cascade}
        busy={cascadeBusy}
        error={cascadeError}
        onRerun={() => void rerunCascade()}
        onClose={() => {
          setCascadeReport(null);
          setCascadeError(null);
        }}
        onOpenDeal={onOpenDeal ? openLinkedDeal : undefined}
      />
    ) : null}
    {/* The Log-activity composer MODAL (2026-08-27 restructure, sanctioned):
        the SAME DealActivityComposer the Activity tab used to host — same
        Note / Log call / Log email lanes, same POST /deals/:id/activities,
        same required-body validation, same TA-06 keep-values-on-failure —
        now inside the shared DialogShell (the existing dialog family, like
        every confirm above; wider clamp so the note toolbar fits on one row).
        Success CLOSES the modal and refetches the detail, so the Activity
        tab's feed already carries the new row when it is next read. A failed
        POST never fires onLogged — the modal stays open with the inline
        error, and resubmitting IS the retry. */}
    {/* The lead-call dialog (2026-09-08). Mounted beside the Log-activity modal
        rather than inside it: this is one flow — record the call, then send the
        link from the control that already owns sending it — and the composer is
        the general-purpose lane that stays available on every card regardless.
        `onLogged` refetches the drawer AND the board, because a call can close
        the deal and the card behind this dialog must not go stale. */}
    {leadCallOpen && detail ? (
      <LeadCallDialog
        deal={deal}
        planLinkLastSentAt={detail.assessment?.planLinkLastSentAt}
        planViewedAt={detail.deal.planViewedAt}
        onClose={() => setLeadCallOpen(false)}
        onLogged={() => {
          load();
          onChanged();
        }}
      />
    ) : null}
    {logOpen ? (
      <DialogShell title="Log activity" onCancel={() => setLogOpen(false)} width="min(600px, 100%)">
        <DealActivityComposer
          dealId={deal.dealId}
          // §9 — server truth from the detail read: the count the NEXT
          // logged call will land on within the active round (null hides
          // the prompt — no round active / older server).
          nextAttempt={roundAttempts !== null ? roundAttempts + 1 : null}
          onLogged={() => {
            setLogOpen(false);
            load();
            onChanged();
          }}
        />
      </DialogShell>
    ) : null}
    </>
  );
}
